import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import "./Home.scss";
import { useSelector, useDispatch } from "react-redux";
import { TextArea } from "../CommonUIComponents/TextArea/TextArea";
import {
  AIService,
  getStoredThreadId,
  setStoredThreadId,
  abortCurrentRequest,
} from "../../api/services/ai.service";
import { UsersService } from "../../api/services/users.service";
import { OrdersService } from "../../api/services/orders.service";
import { SendIcon } from "../../assets/Icons/SendIcon";
import { StopIcon } from "../../assets/Icons/StopIcon";
import { GlobeIcon } from "../../icons/globeIcon";
import { InfoCircleIcon } from "../../assets/Icons/InfoCircleIcon";
import { NavDownArrow } from "../../assets/Icons/NavArrowDown";
import { useLocation, useNavigate } from "react-router-dom";
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
} from "@ey-xd/motif-react";
import { Sparks } from "../../assets/Icons/Sparks";
import { buildInitialMessages } from "./chatScenarios";
import { AIChat } from "../Chats/OrderChats/AIChat/AIChat";
import { UserChat } from "../Chats/OrderChats/UserChat/UserChat";
import { AILoadingCard } from "../Chats/OrderChats/AIChat/AICards/AILoadingCard/AILoadingCard";
import { RecommendedButtonFieldUpdateCard } from "../Chats/OrderChats/AIChat/AICards/RecommendedButtonFieldUpdateCard/RecommendedButtonFieldUpdateCard";
import { TrailLogsSection } from "../Chats/OrderChats/AIChat/AICards/TrailLogsSection/TrailLogsSection";
import { GenericSuperAICard } from "../Chats/SuperChat/SuperChatAI/SuperAICards/GenericSuperAICard/GenericSuperAICard";
import {
  getSuggestionsByTab,
  getRoleBasedInlineSuggestions,
  getRoleBasedPopupSuggestions,
} from "./smartSuggestionsData";
import {
  CHAT,
  CHAT_ACTIONS,
  ARIA_LABELS,
  PLACEHOLDERS,
  SUPER_REGIONS,
} from "../../util/constants";
import {
  setSelectedRegion,
  selectSelectedRegion,
} from "../../store/slices/regionSlice";
import {
  fetchActionRequiredLogs,
  fetchOpenOrdersSummaries,
} from "../../store/slices/ordersSlice";
import { CustomTooltip } from "../CommonUIComponents/CustomTooltip/CustomTooltip";
import { Tooltip } from "../CommonUIComponents/Tooltip/Tooltip";
import { CustomTooltipBottom } from "../CommonUIComponents/CustomTooltip/CustomTooltipBottom";
import { CustomTooltipTop } from "../CommonUIComponents/CustomTooltip/CustomTooltipTop";

// Constants for timing delays
const LOADING_DELAY = 300;
const RESPONSE_DELAY = 500;
const CONTENT_DELAY = 1500;
const SCROLL_DELAY = 150;

// Mapping from smart suggestion text to static_view_type
// These suggestions will skip AI API call and directly fetch orders
// Note: "my weekly orders" is role-dependent and handled separately
const SMART_SUGGESTION_TO_STATIC_VIEW = {
  // Plan my day variations
  "please plan my day": "plan_my_day",
  "plan my day": "plan_my_day",
  // Data triage
  "orders in data triage": "orders_data_triage",
  // Newly allocated
  "newly allocated orders": "newly_allocated_orders",
  // Aging orders
  "aging orders": "aging_orders",
  "ageing orders": "aging_orders",
  "ageing order": "aging_orders",
  // Pending review
  "pending at l1/l2 review": "pending_review",
  // High iteration
  "high iteration orders": "high_iteration_orders",
  // Awaiting response
  "awaiting response from at": "awaiting_response_orders",
  // Orders near due date
  "orders nearing due date": "orders_near_duedate",
  // Allocation review
  "allocation review requested": "allocation_review_request",
  // Action needed
  "action needed": "action_needed_orders",
};

// Helper function to get static_view_type from suggestion text
// selectedRole is used for role-dependent mappings like "my weekly orders"
const getStaticViewTypeFromText = (text, selectedRole) => {
  const normalized = String(text || "")
    .toLowerCase()
    .trim();

  // Handle "my weekly orders" based on role
  if (normalized === "my weekly orders") {
    const roleName =
      typeof selectedRole === "object"
        ? (selectedRole?.roleName || selectedRole?.name || "").toUpperCase()
        : String(selectedRole || "").toUpperCase();

    if (roleName === "REGIONAL LEAD") {
      return "regional_leads_weekly_orders";
    }
    return "my_weekly_orders";
  }

  return SMART_SUGGESTION_TO_STATIC_VIEW[normalized] || null;
};

// Start  BUG-465083
const normalizeOrderStatusEntries = (payload) => {
  const entries =
    payload?.logs?.data?.logs ||
    payload?.logs?.data ||
    payload?.logs ||
    payload?.summaries?.data?.logs ||
    payload?.summaries?.data ||
    payload?.summaries ||
    payload?.data?.logs ||
    payload?.data ||
    payload ||
    [];

  if (Array.isArray(entries)) {
    return entries;
  }

  return entries && typeof entries === "object" ? [entries] : [];
};

const getIsActionRequiredFromPayload = (payload) => {
  const normalizedPayload =
    payload?.logs?.data || payload?.logs || payload?.data || payload;

  if (typeof normalizedPayload?.isActionRequired === "boolean") {
    return normalizedPayload.isActionRequired;
  }

  if (typeof normalizedPayload?.logs?.isActionRequired === "boolean") {
    return normalizedPayload.logs.isActionRequired;
  }

  if (typeof normalizedPayload?.actionRequired === "boolean") {
    return normalizedPayload.actionRequired;
  }

  if (typeof normalizedPayload?.logs?.actionRequired === "boolean") {
    return normalizedPayload.logs.actionRequired;
  }

  return null;
};

const getUpdateOrderValidationWarnings = (response) => {
  // AXON-CHAT-VALIDATION-WARNINGS: normalize warning arrays from resolved updateOrder responses.
  return [response?.validationWarnings, response?.data?.validationWarnings]
    .flatMap((warnings) => (Array.isArray(warnings) ? warnings : []))
    .map((warning) => String(warning || "").trim())
    .filter(Boolean);
};
// End  BUG-465083

// AI Chatbot component for order assistance and suggestions
export const Chatbot = ({
  variant = "home",
  hasStatusHeader = false,
  orderDetails,
  orderId,
  isOpen,
  onPlanMyDay,
  onOrdersInDataTriage,
  onNewlyAllocatedOrders,
  isPlanMyDayMode = false,
  onActionClick,
  onSendToSuperAgent,
  workflowSteps = [],
  onRefreshOrderDetails,
  attachFilesMessages,
  incompleteDataMessage,
  isDisabled = false,
  onAILoadingChange,
  trailRefreshKey,
  notificationNavigationId,
  orderHome = false,
}) => {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(CHAT.AI_SUGGESTIONS);
  const [messages, setMessages] = useState(() =>
    buildInitialMessages({ orderDetails, orderId, variant, workflowSteps }),
  );
  const [updateFieldsFormValues, setUpdateFieldsFormValues] = useState({});
  const [updateFieldsFormSelectedFields, setUpdateFieldsFormSelectedFields] =
    useState([]);
  const [isAILoading, setIsAILoading] = useState(false);
  // Track when "Action Taken" has been clicked to disable buttons on previous cards
  const [actionTakenTimestamp, setActionTakenTimestamp] = useState(null);
  // State for trail logs displayed at top of chat
  const [trailLogs, setTrailLogs] = useState([]);
  // Track if user has interacted with the order (chat messages, field updates, etc.)
  // Once user interacts, chat should be enabled even if opened from notification
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // Start disable recommended action changes 2nd
  // const currentOrderDetailsAiCardId = useMemo(() => {
  //   if (variant !== "order-details") {
  //     return null;
  //   }

  //   const currentAiCard = [...messages]
  //     .reverse()
  //     .find((message) => message.type === "aiCard");

  //   return currentAiCard?.id ?? null;
  // }, [messages, variant]);

  // end disable recommended action changes 2nd

  // Notify parent component when AI loading state changes
  useEffect(() => {
    if (onAILoadingChange) {
      onAILoadingChange(isAILoading);
    }
  }, [isAILoading, onAILoadingChange]);

  // State for region selector dropdown open/close
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);
  const regionDropdownRef = useRef(null);

  // Get selected region from Redux store
  const selectedRegion = useSelector(selectSelectedRegion);

  // Get selected role from Redux store for role-based suggestions
  const selectedRole = useSelector((state) => state.user?.selectedRole);

  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  // Get user info from Redux store
  const user = useSelector((state) => state.user?.user);
  // const userEmail = user?.email || user?.mail || "";
  const userEmail =
    user?.email ||
    user?.mail ||
    user?.username ||
    user?.preferred_username ||
    user?.upn ||
    "";
  //console.log("Chatbot user email:", userEmail);

  // Get action required logs, open orders summaries, and notification changes from Redux store
  const { actionRequiredLogs, openOrdersSummaries, orderChanges } = useSelector(
    (state) => state.orders,
  );

  // Determine source from navigation state - openOrders or actionNeeded
  const normalizeCategory = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();
  const isActionNeededNotification =
    location.state?.source != null
      ? location.state.source === "actionNeeded"
      : normalizeCategory(location.state?.notificationCategory) ===
        "action needed";
  const derivedSource =
    isActionNeededNotification ||
    (Array.isArray(orderDetails)
      ? orderDetails[0]?.headerSection?.[0]?.actionNeeded === true
      : orderDetails?.headerSection?.[0]?.actionNeeded === true)
      ? "actionNeeded"
      : location.state?.source || "openOrders";
  const [sourceOverride, setSourceOverride] = useState(null);
  const source = sourceOverride || derivedSource;

  //start stop Refresh previous AI card Recommended Actions 1st
  const hasSeenNonSummaryRecommendedActionCardRef = useRef(false);
  //end stop Refresh previous AI card Recommended Actions 1st

  // Track last processed incomplete data message to prevent duplicate processing
  const lastProcessedIncompleteDataMessageRef = useRef(null);

  //start stop Refresh previous AI card Recommended Actions 2nd
  const hasInitializedRecommendedActionFreezeRef = useRef(false);

  const freezeInitialSummaryRecommendedActions = useCallback(() => {
    if (variant !== "order-details") {
      return;
    }

    const hasInitialSummaryCard = messages.some((msg) =>
      msg.id?.startsWith("m_summary_"),
    );

    if (hasInitialSummaryCard) {
      hasSeenNonSummaryRecommendedActionCardRef.current = true;
    }
  }, [messages, variant]);

  //end stop Refresh previous AI card Recommended Actions 2nd

  // Start disable recommended action changes 2nd
  const latestRecommendedActionCardId = useMemo(() => {
    //new
    if (variant !== "order-details") {
      return null;
    }

    const hasRecommendedActionPayload = (recommendedActions) => {
      if (Array.isArray(recommendedActions)) {
        return recommendedActions.some((item) => {
          const label =
            typeof item === "string"
              ? item
              : item?.label || item?.value || item?.name || "";

          return String(label || "").trim().length > 0;
        });
      }

      if (
        !recommendedActions ||
        typeof recommendedActions !== "object" ||
        recommendedActions.isEmpty === true
      ) {
        return false;
      }

      return Object.entries(recommendedActions).some(
        ([key, values]) =>
          key !== "isEmpty" &&
          Array.isArray(values) &&
          values.some((value) => String(value || "").trim().length > 0),
      );
    };

    const currentOrderStatus = Array.isArray(orderDetails)
      ? orderDetails[0]?.headerSection?.[0]?.currentSubStage ||
        orderDetails[0]?.status ||
        ""
      : orderDetails?.headerSection?.[0]?.currentSubStage ||
        orderDetails?.status ||
        "";
    const analyzerType = Array.isArray(orderDetails)
      ? orderDetails[0]?.headerSection?.[0]?.analyzer ||
        orderDetails[0]?.headerSection?.[0]?.analyzerType ||
        ""
      : orderDetails?.headerSection?.[0]?.analyzer ||
        orderDetails?.headerSection?.[0]?.analyzerType ||
        "";
    const canAppendAttachFilesAction =
      String(currentOrderStatus || "")
        .trim()
        .toLowerCase() === "data received" &&
      String(analyzerType || "")
        .trim()
        .toLowerCase() === "general ledger analyzer";

    const hasRecommendedActions = (message) => {
      // Start disable recommended action changes 3rd
      const hasExplicitUiFields =
        Array.isArray(message?.ui_fields) && message.ui_fields.length > 0;
      const hasFlagDrivenUiFields =
        (message?.efforts_flag || message?.savings_flag) &&
        Array.isArray(orderDetails) &&
        orderDetails.length > 0;
      // End disable recommended action changes 3rd
      if (
        message?.type !== "aiCard" ||
        message?.suppressRecommendedActions === true
      ) {
        return false;
      }

      // Start disable recommended action changes 3rd
      if (
        message.loading ||
        message.attachFilesCard ||
        message.updateOrderInfo ||
        message.effortsSavingsForm ||
        message.updateMultipleFields ||
        message.updateSingleField ||
        message.updateFieldsForm ||
        message.updateFieldsFormFailed ||
        message.activityLog ||
        (message.ui_fields_flag && hasExplicitUiFields) ||
        hasFlagDrivenUiFields
      ) {
        return false;
      }

      if (message.updateFieldSuccess || message.updateFieldsFormSuccess) {
        return true;
      }
      // end disable recommended action changes 3rd

      if (message.updateFieldFailed) {
        return (
          message.showRecommendedActions === true &&
          hasRecommendedActionPayload(message.recommendedActions)
        );
      }

      if (
        !message.showRecommendedActions &&
        !message.isInitialOrderDetailsResponse
      ) {
        return false;
      }

      return (
        hasRecommendedActionPayload(message.recommendedActions) ||
        message.source === "actionNeeded" ||
        (!message.updateFieldFailed && canAppendAttachFilesAction)
      );
    };

    return [...messages].reverse().find(hasRecommendedActions)?.id ?? null;
  }, [messages, orderDetails, variant]);

  // end disable recommended action changes 2nd

  //start stop Refresh previous AI card Recommended Actions 1st
  useEffect(() => {
    if (
      variant === "order-details" &&
      latestRecommendedActionCardId &&
      !latestRecommendedActionCardId.startsWith("m_summary_")
    ) {
      hasSeenNonSummaryRecommendedActionCardRef.current = true;
    }
  }, [latestRecommendedActionCardId, variant]);
  //end stop Refresh previous AI card Recommended Actions 1st

  useEffect(() => {
    setSourceOverride(null);
  }, [
    orderId,
    path,
    location.state?.source,
    location.state?.notificationCategory,
  ]);

  //start stop Refresh previous AI card Recommended Actions 1st
  useEffect(() => {
    if (!hasInitializedRecommendedActionFreezeRef.current) {
      hasInitializedRecommendedActionFreezeRef.current = true;
      return;
    }

    hasSeenNonSummaryRecommendedActionCardRef.current = false;
  }, [orderId, notificationNavigationId, path]);
  //end stop Refresh previous AI card Recommended Actions 1st

  // Check if order was opened from notification with chat restrictions
  // (non-action-needed, non-latest notification for non-outlook_agent orders)
  const fromNotification = Boolean(location.state?.fromNotification);
  const shouldRestrictChatFromNav = Boolean(location.state?.shouldRestrictChat);

  // Get agentType from order details
  const orderAgentType = useMemo(() => {
    const agentType = Array.isArray(orderDetails)
      ? orderDetails[0]?.headerSection?.[0]?.agentType ||
        orderDetails[0]?.agentType ||
        ""
      : orderDetails?.headerSection?.[0]?.agentType ||
        orderDetails?.agentType ||
        "";
    return String(agentType || "")
      .trim()
      .toLowerCase();
  }, [orderDetails]);

  const isOutlookAgentOrder = orderAgentType === "outlook_agent";

  // Get orderActivityType from order details to check for disabled states
  const orderActivityType = useMemo(() => {
    const activityType = Array.isArray(orderDetails)
      ? orderDetails[0]?.headerSection?.[0]?.orderActivityType || ""
      : orderDetails?.headerSection?.[0]?.orderActivityType || "";
    return String(activityType || "")
      .trim()
      .toLowerCase();
  }, [orderDetails]);

  // Check if order is in a state that should disable chat
  const isOrderInDisabledState = useMemo(() => {
    const disabledStates = ["cancelled", "tool issue", "on hold"];
    return disabledStates.includes(orderActivityType);
  }, [orderActivityType]);

  // Determine if chat should show static notification summary (skip AI call)
  // This should ONLY happen for notification-based restrictions, NOT for order activity type restrictions
  const shouldShowStaticNotificationSummary = useMemo(() => {
    // If user has already interacted with this order, never show static summary
    if (hasUserInteracted) return false;

    if (!fromNotification) return false;

    const normalizedCategory = String(
      location.state?.notificationCategory || "",
    )
      .trim()
      .toLowerCase();
    const isActionNeeded = normalizedCategory === "action needed";

    // Action needed notifications never show static summary
    if (isActionNeeded) return false;

    // For non-action-needed notifications:
    // shouldRestrictChatFromNav is true when NOT latest
    // Show static summary if (not latest) OR (not outlook_agent)
    return shouldRestrictChatFromNav || (!isOutlookAgentOrder && orderAgentType !== "allocation_agent");
  }, [
    fromNotification,
    shouldRestrictChatFromNav,
    isOutlookAgentOrder,
    orderAgentType,
    location.state?.notificationCategory,
    hasUserInteracted,
  ]);

  // Determine if chat input should be disabled
  // This includes both notification-based restrictions AND order activity type restrictions
  // IMPORTANT: Once user interacts with the order (sends message, updates fields), enable chat
  // ALSO: Disable chat for orders with orderActivityType = "Cancelled", "Tool Issue", or "On Hold"
  const isChatDisabled = useMemo(() => {
    // Always disable chat for orders in disabled states (Cancelled, Tool Issue, On Hold)
    if (isOrderInDisabledState) return true;

    // Use the notification-based restriction logic
    return shouldShowStaticNotificationSummary;
  }, [isOrderInDisabledState, shouldShowStaticNotificationSummary]);

  // Use the appropriate summaries based on source
  const activeSummaries =
    source === "openOrders" ? openOrdersSummaries : actionRequiredLogs;

  const orderAxonId = useMemo(() => {
    if (Array.isArray(orderDetails)) {
      return orderDetails[0]?.headerSection?.[0]?.axonId || null;
    }

    return orderDetails?.headerSection?.[0]?.axonId || null;
  }, [orderDetails]);

  // Extract order status fields for order chat API calls
  // These fields are passed as status_fields in the query payload for order chat context
  const orderStatusFields = useMemo(() => {
    if (variant !== "order-details" || !orderDetails) {
      console.log(
        "[Chatbot] orderStatusFields returning null - variant:",
        variant,
        "hasOrderDetails:",
        !!orderDetails,
      );
      return null;
    }

    const headerSection = Array.isArray(orderDetails)
      ? orderDetails[0]?.headerSection?.[0]
      : orderDetails?.headerSection?.[0];

    if (!headerSection) {
      console.log(
        "[Chatbot] orderStatusFields returning null - no headerSection found",
      );
      return null;
    }

    console.log("[Chatbot] orderStatusFields computed successfully");

    // Extract all sections to find date fields like DueDate, AuditFilingDate
    const allSections = orderDetails;

    // Helper to find field value from any section
    const findFieldValue = (fieldName) => {
      const normalizedFieldName = fieldName
        .toLowerCase()
        .replace(/[\s_-]+/g, "");

      // Safely access section and pagefields
      try {
        const section = allSections?.[3];
        if (section?.pagefields && Array.isArray(section.pagefields)) {
          for (const field of section.pagefields) {
            const fieldKey = String(field?.field || "")
              .toLowerCase()
              .replace(/[\s_-]+/g, "");
            if (
              fieldKey === normalizedFieldName ||
              fieldKey.includes(normalizedFieldName)
            ) {
              return (
                field?.value ||
                field?.displayValue ||
                field?.fieldValue ||
                field?.field_value ||
                ""
              );
            }
          }
        }
      } catch (e) {
        console.warn("Error finding field value:", e);
      }

      return "";
    };

    return {
      currentSubstage:
        headerSection.currentSubStage || headerSection.status || "",
      orderId: headerSection.axonId || headerSection.requestId || "",
      analyzerType: headerSection.analyzer || headerSection.analyzerType || "",
      dueDate:
        headerSection.dueDateString ||
        headerSection.dueDate ||
        findFieldValue("DueDate") ||
        "",
      auditFilingDate:
        findFieldValue("AuditReportDate") ||
        findFieldValue("AuditFilingDate") ||
        "",
      ageing:
        headerSection.ageing ||
        headerSection.agingIn ||
        headerSection.agingInDays ||
        "",
      dataTriageSubstatus: headerSection.dataTriageSubStage || "",
    };
  }, [variant, orderDetails]);

  // Thread ID is shared across Super Agent and Order Agent chats for the
  // current browser session. It starts as null (new session) and is set from
  // the AI response, persisted in sessionStorage by ai.service.
  const threadIdRef = useRef(getStoredThreadId());

  // Track if initial summary has been fetched for current orderId
  const initialSummaryFetchedRef = useRef(null);
  /** `${orderId}|${notificationNavigationId ?? ""}` — bumps on same-order notification opens */
  const orderDetailsChatInitMarkerRef = useRef("");
  // Track the last processed notificationNavigationId to detect when it changes
  const lastNotificationNavIdRef = useRef(null);
  // Track if a fetch is currently in progress to prevent duplicate calls
  const fetchInProgressRef = useRef(false);
  // Track the current fetch key to detect stale fetches
  const currentFetchKeyRef = useRef(null);

  // Store parsed email summaries and recommended actions for use when creating messages
  const parsedEmailSummariesRef = useRef([]);
  const parsedRecommendedActionsRef = useRef([]);
  const parsedActionTakenSummaryRef = useRef(null);
  const parsedActionTakenListRef = useRef([]);
  const parsedTrailLogsRef = useRef([]);

  // Function to refresh trail logs
  const refreshTrailLogs = useCallback(async () => {
    try {
      // Use axonId for the updatetrail API endpoint
      const axonId = Array.isArray(orderDetails)
        ? orderDetails[0]?.headerSection?.[0]?.axonId || orderId
        : orderDetails?.headerSection?.[0]?.axonId || orderId;

      if (!axonId) return;

      const trailResponse = await OrdersService.getOrderTrial(axonId);
      if (trailResponse?.success && trailResponse?.data?.trailLogs) {
        setTrailLogs(trailResponse.data.trailLogs);
        parsedTrailLogsRef.current = trailResponse.data.trailLogs;
      }
    } catch (trailError) {
      console.error("Failed to refresh trail logs:", trailError);
    }
  }, [orderDetails, orderId]);

  // Re-fetch the update trail whenever the parent signals a refresh
  // (e.g. after any successful OrdersService.updateOrder call).
  const prevTrailRefreshKeyRef = useRef(trailRefreshKey);
  useEffect(() => {
    if (prevTrailRefreshKeyRef.current === trailRefreshKey) return;
    prevTrailRefreshKeyRef.current = trailRefreshKey;
    if (trailRefreshKey === undefined || trailRefreshKey === null) return;
    refreshTrailLogs();
  }, [trailRefreshKey, refreshTrailLogs]);

  // Helper to parse action taken summary which may come as JSON like {"Summary":"..."}
  // Returns a formatted string with key and value, e.g., "Summary: Assigned Preparer..."
  const parseActionTakenSummary = useCallback((rawSummary) => {
    if (!rawSummary) return null;

    let summaryStr = String(rawSummary).trim();
    if (!summaryStr) return null;

    // Try to parse as JSON if it looks like JSON
    if (summaryStr.startsWith("{") && summaryStr.endsWith("}")) {
      try {
        const parsed = JSON.parse(summaryStr);
        // Format all key-value pairs from the JSON object
        if (parsed && typeof parsed === "object") {
          const formattedParts = Object.entries(parsed)
            .filter(
              ([key, value]) =>
                value !== null && value !== undefined && value !== "",
            )
            .map(([key, value]) => {
              // Clean up any escaped quotes or unicode from the value
              const cleanValue = String(value)
                .replace(/\\u0022/g, '"')
                .replace(/^\"|\"$/g, "")
                .trim();
              return `${key}: ${cleanValue}`;
            });

          if (formattedParts.length > 0) {
            return formattedParts.join("\n");
          }
        }
      } catch (e) {
        // Not valid JSON, continue with string processing
      }
    }

    // Clean up any escaped quotes or unicode in the raw string
    return (
      summaryStr
        .replace(/\\u0022/g, '"')
        .replace(/^\"|\"$/g, "")
        .trim() || null
    );
  }, []);

  const parseOrderContextFromLogs = useCallback(
    (logSource) => {
      let emailSummaries = [];
      let recommendedActions = null;
      let actionTakenSummary = null;
      let actionTakenList = [];

      const normalizedLogSource =
        source === "openOrders"
          ? logSource?.summaries?.data?.logs ||
            logSource?.summaries?.data ||
            logSource?.summaries ||
            logSource
          : logSource?.logs?.data?.logs ||
            logSource?.logs?.data ||
            logSource?.logs ||
            logSource;

      const normalizedEntries = Array.isArray(normalizedLogSource)
        ? normalizedLogSource
        : normalizedLogSource && typeof normalizedLogSource === "object"
          ? [normalizedLogSource]
          : [];

      const logEntry =
        normalizedEntries.length > 0 ? normalizedEntries[0] : null;
      const logPayload =
        logEntry?.logs && typeof logEntry.logs === "object"
          ? logEntry.logs
          : logEntry;

      // Get action taken from API response (not from notification changes)
      if (logPayload?.actionTakenSummary) {
        actionTakenSummary = parseActionTakenSummary(
          logPayload.actionTakenSummary,
        );
      }

      // Parse email summaries - support both openOrders and actionNeeded sources
      if (logPayload?.emailFrom && logPayload?.summary) {
        emailSummaries = [
          {
            sender: logPayload.emailFrom,
            summary: logPayload.summary,
            date: logPayload.lastUpdated || "", // Use lastUpdated for timestamp
          },
        ];
      } else if (logPayload?.emailSummary) {
        try {
          let emailSummaryValue = logPayload.emailSummary;

          // Handle case where emailSummary is a string that needs to be parsed
          // It might be double-encoded or a raw JSON string
          if (typeof emailSummaryValue === "string") {
            emailSummaryValue = emailSummaryValue.trim();

            // Remove any leading/trailing brackets and quotes if it's a malformed string
            // Check if it looks like a JSON array string
            if (
              emailSummaryValue.startsWith("[") &&
              emailSummaryValue.endsWith("]")
            ) {
              try {
                emailSummaryValue = JSON.parse(emailSummaryValue);
              } catch {
                // If parsing fails, try to clean up the string manually
                // Remove the outer brackets and split by the pattern
                let cleanedStr = emailSummaryValue.slice(1, -1).trim();
                // Remove leading/trailing quotes if present
                if (cleanedStr.startsWith('"') && cleanedStr.endsWith('"')) {
                  cleanedStr = cleanedStr.slice(1, -1);
                }
                // Handle escaped quotes
                cleanedStr = cleanedStr.replace(/\\"/g, '"');
                emailSummaryValue = [cleanedStr];
              }
            }
          }

          const parsedEmails = Array.isArray(emailSummaryValue)
            ? emailSummaryValue
            : [emailSummaryValue];

          // If parsed as array of objects with sender/summary, use directly
          // Otherwise wrap each item appropriately
          if (Array.isArray(parsedEmails)) {
            emailSummaries = parsedEmails.map((item) => {
              if (
                item &&
                typeof item === "object" &&
                (item.sender || item.summary)
              ) {
                return {
                  sender: item.sender || item.emailFrom || "",
                  summary: item.summary || "",
                  date: item.date || item.lastUpdated || "",
                };
              }
              // If it's a string in format "email, date, summary", parse it
              if (typeof item === "string") {
                let itemStr = item.trim();
                // Remove any remaining escaped quotes
                itemStr = itemStr.replace(/\\"/g, '"').replace(/^"|"$/g, "");

                // Check for comma-separated format: "sender, date, summary"
                const commaIndex = itemStr.indexOf(",");
                if (commaIndex > -1) {
                  const firstPart = itemStr.substring(0, commaIndex).trim();
                  const rest = itemStr.substring(commaIndex + 1).trim();
                  const secondCommaIndex = rest.indexOf(",");
                  if (secondCommaIndex > -1) {
                    const datePart = rest.substring(0, secondCommaIndex).trim();
                    const summaryPart = rest
                      .substring(secondCommaIndex + 1)
                      .trim();
                    return {
                      sender: firstPart,
                      date: datePart,
                      summary: summaryPart,
                    };
                  } else {
                    return { sender: firstPart, summary: rest, date: "" };
                  }
                }
                return { sender: "", summary: itemStr, date: "" };
              }
              return { sender: "", summary: String(item || ""), date: "" };
            });
          } else {
            emailSummaries = [];
          }
        } catch (e) {
          // Not valid JSON - try to parse as comma-separated format or quoted strings
          const emailSummaryStr = String(logPayload.emailSummary).trim();

          // Check if it's comma-separated format: "sender, date, summary"
          if (
            emailSummaryStr.includes(",") &&
            !emailSummaryStr.startsWith('"')
          ) {
            const parts = emailSummaryStr.split(",").map((p) => p.trim());
            if (parts.length >= 3) {
              emailSummaries = [
                {
                  sender: parts[0],
                  date: parts[1],
                  summary: parts.slice(2).join(",").trim(),
                },
              ];
            } else if (parts.length === 2) {
              emailSummaries = [
                {
                  sender: parts[0],
                  summary: parts[1],
                  date: logPayload.lastUpdated || "",
                },
              ];
            } else {
              emailSummaries = [
                {
                  sender: "",
                  summary: emailSummaryStr,
                  date: logPayload.lastUpdated || "",
                },
              ];
            }
          } else {
            // Try regex for quoted strings - treat each as a sender email
            const emailMatches = emailSummaryStr.match(/"([^"]+)"/g);
            if (emailMatches) {
              // When we only have quoted email addresses, use the whole summary string as context
              emailSummaries = [
                {
                  sender: emailMatches[0]?.replace(/"/g, "").trim() || "",
                  summary: emailSummaryStr,
                  date: logPayload.lastUpdated || "",
                },
              ];
            } else if (emailSummaryStr) {
              // Fallback: treat the entire string as summary
              emailSummaries = [
                {
                  sender: "",
                  summary: emailSummaryStr,
                  date: logPayload.lastUpdated || "",
                },
              ];
            }
          }
        }
      }

      // Process recommended actions - always check for actual action arrays
      // regardless of the isEmpty flag from API, since API may return isEmpty:true
      // even when valid actions exist
      if (
        logPayload?.recommendedActions &&
        typeof logPayload.recommendedActions === "object" &&
        !Array.isArray(logPayload.recommendedActions)
      ) {
        const normalizedActions = { isEmpty: false };

        Object.entries(logPayload.recommendedActions).forEach(
          ([key, value]) => {
            // Skip isEmpty key and non-array values
            if (key === "isEmpty" || !Array.isArray(value)) {
              return;
            }

            const normalizedValues = value
              .map((item) => String(item || "").trim())
              .filter((item) => item.length > 0);

            if (normalizedValues.length > 0) {
              normalizedActions[key] = normalizedValues;
            }
          },
        );

        const hasAnyActionValues = Object.keys(normalizedActions).some(
          (key) => key !== "isEmpty",
        );

        if (hasAnyActionValues) {
          // Ensure isEmpty is always false when we have actual action values
          normalizedActions.isEmpty = false;
          recommendedActions = normalizedActions;
        }
      }

      return {
        emailSummaries,
        recommendedActions,
        actionTakenSummary,
        actionTakenList,
      };
    },
    [source],
  );

  // Close region dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        regionDropdownRef.current &&
        !regionDropdownRef.current.contains(event.target)
      ) {
        setIsRegionDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Initialize / reset order-details chat when axon order id or notification context changes
  useEffect(() => {
    if (variant !== "order-details" || !orderId || !orderDetails) {
      return;
    }

    // Use consistent key format with the fetch effect
    const marker = `${orderId}|${notificationNavigationId ?? "direct"}`;

    // Check if notificationNavigationId changed - this indicates a new notification click
    // for potentially the same order. We need to reset even if orderDetails briefly went null.
    const navIdChanged =
      notificationNavigationId !== lastNotificationNavIdRef.current &&
      lastNotificationNavIdRef.current !== null &&
      notificationNavigationId !== null;

    if (navIdChanged) {
      // Reset the marker to force re-initialization
      orderDetailsChatInitMarkerRef.current = "";
    }

    // Update the tracked navigation ID
    lastNotificationNavIdRef.current = notificationNavigationId ?? null;

    if (orderDetailsChatInitMarkerRef.current === marker) {
      return;
    }
    orderDetailsChatInitMarkerRef.current = marker;

    // Reset user interaction state when order changes
    setHasUserInteracted(false);

    // Reset the fetch refs so the fetch effect will run for this new notification
    // This is the ONLY place we reset them - the fetch effect will set them when it starts
    initialSummaryFetchedRef.current = null;
    fetchInProgressRef.current = false;
    // Clear current fetch key to mark any in-flight fetches as stale
    currentFetchKeyRef.current = null;

    // When opening from notification, parse notification details IMMEDIATELY
    // to populate refs BEFORE fetchOrderSummary runs
    if (fromNotification && location?.state?.notificationDetails) {
      const notificationDetails = location?.state?.notificationDetails;
      const details = notificationDetails?.data || notificationDetails;

      // Parse email summary from notification details for all notification types
      let emailSummaries = [];
      if (details?.emailFrom && details?.summary) {
        emailSummaries = [
          {
            sender: details.emailFrom,
            summary: details.summary,
            date: details.lastUpdated || details.createdDate || "",
          },
        ];
      } else if (details?.emailSummary) {
        const emailSummaryStr = String(details.emailSummary).trim();
        try {
          const parsedEmails = JSON.parse(emailSummaryStr);
          if (Array.isArray(parsedEmails)) {
            emailSummaries = parsedEmails.map((item) => {
              if (
                item &&
                typeof item === "object" &&
                (item.sender || item.summary)
              ) {
                return {
                  sender: item.sender || item.emailFrom || "",
                  summary: item.summary || "",
                  date: item.date || item.lastUpdated || "",
                };
              }
              // If it's a string in format "email, date, summary", parse it
              if (typeof item === "string") {
                const itemStr = item.trim();
                const commaIndex = itemStr.indexOf(",");
                if (commaIndex > -1) {
                  const firstPart = itemStr.substring(0, commaIndex).trim();
                  const rest = itemStr.substring(commaIndex + 1).trim();
                  const secondCommaIndex = rest.indexOf(",");
                  if (secondCommaIndex > -1) {
                    const datePart = rest.substring(0, secondCommaIndex).trim();
                    const summaryPart = rest
                      .substring(secondCommaIndex + 1)
                      .trim();
                    return {
                      sender: firstPart,
                      date: datePart,
                      summary: summaryPart,
                    };
                  } else {
                    return { sender: firstPart, summary: rest, date: "" };
                  }
                }
                return { sender: "", summary: itemStr, date: "" };
              }
              return { sender: "", summary: String(item || ""), date: "" };
            });
          } else {
            emailSummaries = [];
          }
        } catch (e) {
          const emailMatches = emailSummaryStr.match(/"([^"]+)"/g);
          if (emailMatches) {
            emailSummaries = emailMatches.map((match) =>
              match.replace(/"/g, "").trim(),
            );
          } else {
            // Handle comma-separated format: "email, date, summary"
            const parts = emailSummaryStr.split(",").map((p) => p.trim());
            if (parts.length >= 3) {
              const sender = parts[0];
              const date = parts[1];
              const summary = parts.slice(2).join(",").trim();
              emailSummaries = [{ sender, summary, date }];
            } else if (parts.length === 2) {
              emailSummaries = [
                {
                  sender: parts[0],
                  summary: parts[1],
                  date: details.createdDate || "",
                },
              ];
            } else if (emailSummaryStr) {
              emailSummaries = [
                {
                  sender: "",
                  summary: emailSummaryStr,
                  date: details.createdDate || "",
                },
              ];
            }
          }
        }
      }

      // Parse action taken summary
      const actionTakenSummary = details?.actionTakenSummary
        ? parseActionTakenSummary(details.actionTakenSummary)
        : null;

      // Parse action taken list
      const actionTakenList =
        details?.actionTakenList && Array.isArray(details.actionTakenList)
          ? details.actionTakenList
          : [];

      parsedEmailSummariesRef.current = emailSummaries;
      parsedActionTakenSummaryRef.current = actionTakenSummary;
      parsedActionTakenListRef.current = actionTakenList;
      // recommended actions will be populated from logs API
      parsedRecommendedActionsRef.current = [];
    } else {
      // Normal flow - reset refs
      parsedEmailSummariesRef.current = [];
      parsedActionTakenSummaryRef.current = null;
      parsedActionTakenListRef.current = [];
      parsedRecommendedActionsRef.current = [];
    }

    parsedTrailLogsRef.current = [];
    threadIdRef.current = getStoredThreadId();

    const initialMessages = buildInitialMessages({
      orderDetails,
      orderId,
      variant,
      workflowSteps,
    });
    setMessages(initialMessages.length > 0 ? initialMessages : []);
  }, [
    orderDetails,
    orderId,
    variant,
    workflowSteps,
    notificationNavigationId,
    fromNotification,
    location?.state?.notificationDetails,
  ]);

  // Fetch AI summary when entering an order details page
  // This effect waits for order details to be fully loaded (including orderStatusFields) before calling the query API
  useEffect(() => {
    // Only fetch if we have all required data
    if (variant !== "order-details" || !orderId || !orderDetails) {
      console.log("[Chatbot] AI query skipped - missing required data:", {
        variant,
        orderId,
        hasOrderDetails: !!orderDetails,
      });
      // When orderDetails becomes null/undefined while we're in order-details mode,
      // reset the fetch refs so a fresh fetch can happen when data becomes available
      // Also turn off loading to prevent infinite loading state
      if (variant === "order-details" && orderId && !orderDetails) {
        initialSummaryFetchedRef.current = null;
        fetchInProgressRef.current = false;
        currentFetchKeyRef.current = null;
        setIsAILoading(false);
      }
      return;
    }

    // For non-disabled chat, we MUST wait for orderStatusFields to be available
    // This ensures the query API gets the necessary status fields for proper summary generation
    if (!isChatDisabled && !orderStatusFields) {
      console.log(
        "[Chatbot] AI query skipped - waiting for orderStatusFields. isChatDisabled:",
        isChatDisabled,
        "orderStatusFields:",
        orderStatusFields,
      );
      return;
    }

    console.log("[Chatbot] AI query conditions met - proceeding with fetch");

    // Include notificationNavigationId so different notifications for the same order trigger a fresh fetch
    const fetchKey = `${orderId}|${notificationNavigationId ?? "direct"}`;

    // If this is a NEW fetch key, cancel any previous in-progress fetch
    if (currentFetchKeyRef.current !== fetchKey) {
      // Reset state for new order/notification
      fetchInProgressRef.current = false;
      initialSummaryFetchedRef.current = null;
    }

    // Check if we've already fetched for this exact key
    if (initialSummaryFetchedRef.current === fetchKey) {
      return;
    }

    // Set current fetch key to track this fetch attempt
    // NOTE: initialSummaryFetchedRef is set INSIDE fetchOrderSummary after staleness check passes
    // This ensures that if the timeout is cleared before fetch runs, we don't mark it as "fetched"
    currentFetchKeyRef.current = fetchKey;
    fetchInProgressRef.current = true;

    // Capture the fetch key for staleness check inside async function
    const thisFetchKey = fetchKey;

    // Show loader immediately when we decide to fetch
    setIsAILoading(true);

    const fetchOrderSummary = async () => {
      try {
        // Check if this fetch is still the current one (not stale)
        if (currentFetchKeyRef.current !== thisFetchKey) {
          console.log(
            `Skipping stale fetch for ${thisFetchKey}, current is ${currentFetchKeyRef.current}`,
          );
          // Another fetch is now active, so we don't turn off loading (let the new fetch handle it)
          return;
        }

        // Mark as fetched only AFTER we've confirmed this fetch is not stale
        // This prevents the issue where a cleared timeout would mark the fetch as done
        initialSummaryFetchedRef.current = thisFetchKey;

        // Fetch trail logs in parallel with AI summary
        // Use axonId for the updatetrail API endpoint
        const axonId = Array.isArray(orderDetails)
          ? orderDetails[0]?.headerSection?.[0]?.axonId || orderId
          : orderDetails?.headerSection?.[0]?.axonId || orderId;

        // Check if this fetch is stale before continuing
        if (currentFetchKeyRef.current !== thisFetchKey) {
          console.log(`Aborting stale fetch for ${thisFetchKey}`);
          return;
        }

        // Fetch trail logs
        try {
          const trailResponse = await OrdersService.getOrderTrial(axonId);
          // Check staleness again after async call
          if (currentFetchKeyRef.current !== thisFetchKey) return;

          if (trailResponse?.success && trailResponse?.data?.trailLogs) {
            parsedTrailLogsRef.current = trailResponse.data.trailLogs;
            setTrailLogs(trailResponse.data.trailLogs);
          }
        } catch (trailError) {
          console.error("Failed to fetch trail logs:", trailError);
          // Don't fail the whole request if trail logs fail
        }

        // Check staleness before continuing
        if (currentFetchKeyRef.current !== thisFetchKey) return;

        // When navigating from older notification for non-outlook orders,
        // skip AI API call and show static summary card with available data
        // Note: This should NOT trigger for orders in disabled states (Cancelled/Tool Issue/On Hold)
        // opened from order table - those should still get the AI response
        if (shouldShowStaticNotificationSummary) {
          const staticSummaryMessage = {
            id: `m_summary_${Date.now()}`,
            role: "ai",
            type: "aiCard",
            isInitialOrderDetailsResponse: true,
            header: { title: "GDS.ai Axon" },
            text: "Here is the summary for this notification.",
            emailSummaries: parsedEmailSummariesRef.current,
            recommendedActions: [], // Don't show recommended actions when chat is disabled
            showRecommendedActions: false,
            actionTakenSummary: parsedActionTakenSummaryRef.current,
            actionTakenList: parsedActionTakenListRef.current,
            trailLogs: parsedTrailLogsRef.current,
            source: source,
            timestamp: Date.now(),
            suppressRecommendedActions: true,
          };

          // Only add message if this fetch is still the current one
          if (currentFetchKeyRef.current === thisFetchKey) {
            setMessages((prev) => [...prev, staticSummaryMessage]);
          }
          return;
        }

        // orderStatusFields is guaranteed to be available here since we wait for it before entering this flow
        const summaryQuery = `Please provide a brief summary for order ${orderId}`;

        const response = await AIService.sendMessage({
          message: summaryQuery,
          threadId: threadIdRef.current,
          userId: userEmail,
          orderId: orderId,
          region: selectedRegion?.id || "general",
          ...(orderStatusFields && { statusFields: orderStatusFields }),
        });

        console.log(
          "[Chatbot] AI Query API Response (Initial Summary):",
          response,
        );

        // Don't update state if a different fetch has started (order/notification changed)
        if (currentFetchKeyRef.current !== thisFetchKey) {
          console.log(`Ignoring stale response for ${thisFetchKey}`);
          return;
        }

        // Check if response has update: true or refresh_flag: true and refresh order details
        if (
          (response?.update === true || response?.refresh_flag === true) &&
          onRefreshOrderDetails
        ) {
          onRefreshOrderDetails();
        }

        const aiSummaryMessage = {
          id: `m_summary_${Date.now()}`,
          role: "ai",
          type: "aiCard",
          isInitialOrderDetailsResponse: true,
          header: { title: "GDS.ai Axon" },
          text:
            response?.response ||
            response?.message ||
            response?.answer ||
            "Here's your order summary.",
          emailSummaries: parsedEmailSummariesRef.current,
          recommendedActions: parsedRecommendedActionsRef.current,
          showRecommendedActions: true,
          actionTakenSummary: parsedActionTakenSummaryRef.current,
          actionTakenList: parsedActionTakenListRef.current,
          trailLogs: parsedTrailLogsRef.current,
          source: source,
          timestamp: Date.now(),
          // Include ui_fields_flag and ui_fields from response if present
          ...((response?.ui_fields_flag ||
            response?.efforts_flag ||
            response?.savings_flag) && {
            ui_fields_flag: true,
            ui_fields: response.ui_fields || [],
            ...(response?.efforts_flag === true && { efforts_flag: true }),
            ...(response?.savings_flag === true && { savings_flag: true }),
          }),
          ...(response?.checkpoint_pending === true && {
            checkpoint_pending: true,
          }),
        };

        // Add the AI response to messages
        setMessages((prev) => [...prev, aiSummaryMessage]);
      } catch (error) {
        // Check if request was aborted via kill switch - don't show error message
        if (error.name === "AbortError" || error?.aborted) {
          console.log(
            "[Chatbot] Request was aborted, not showing error message",
          );
          return;
        }

        // Don't show error if a different fetch has started
        if (currentFetchKeyRef.current !== thisFetchKey) {
          return;
        }
        console.error("Failed to fetch order summary:", error);
        const hasEmailSummaries =
          Array.isArray(parsedEmailSummariesRef.current) &&
          parsedEmailSummariesRef.current.length > 0;
        const hasActionTakenSummary =
          !!parsedActionTakenSummaryRef.current ||
          (Array.isArray(parsedActionTakenListRef.current) &&
            parsedActionTakenListRef.current.length > 0);
        const hasRecommendedActions = (() => {
          const actions = parsedRecommendedActionsRef.current;
          if (!actions) return false;
          if (Array.isArray(actions)) return actions.length > 0;
          if (typeof actions === "object") {
            if (actions.isEmpty !== false) {
              return false;
            }
            return Object.entries(actions).some(
              ([key, value]) =>
                key !== "isEmpty" && Array.isArray(value) && value.length > 0,
            );
          }
          return false;
        })();

        const hasAnyCardSection =
          hasEmailSummaries || hasActionTakenSummary || hasRecommendedActions;
        const aiErrorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "I'm having trouble fetching the order summary. Please try again later.";

        const errorMessage = {
          id: `m_summary_${Date.now()}`,
          role: "ai",
          type: "aiCard",
          isInitialOrderDetailsResponse: true,
          header: { title: "GDS.ai Axon" },
          text: hasAnyCardSection
            ? "Here are the latest available updates for this order."
            : aiErrorMessage,
          emailSummaries: parsedEmailSummariesRef.current,
          recommendedActions: parsedRecommendedActionsRef.current,
          showRecommendedActions: true,
          actionTakenSummary: parsedActionTakenSummaryRef.current,
          actionTakenList: parsedActionTakenListRef.current,
          source: source,
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        // Only turn off loading if this is still the current fetch
        // This prevents a stale fetch from turning off the loader for a new fetch
        if (currentFetchKeyRef.current === thisFetchKey) {
          fetchInProgressRef.current = false;
          setIsAILoading(false);
        }
      }
    };

    // Fetch summary immediately - no delay needed since we have proper staleness checks
    const timeoutId = setTimeout(fetchOrderSummary, 100);
    return () => {
      clearTimeout(timeoutId);
      // Don't reset fetchInProgressRef here - the new effect will handle it
    };
  }, [
    variant,
    orderId,
    orderDetails,
    userEmail,
    notificationNavigationId,
    shouldShowStaticNotificationSummary,
    orderStatusFields,
    selectedRegion?.id,
    isChatDisabled,
  ]);

  // Handle attach files messages from Files tab click
  useEffect(() => {
    if (
      attachFilesMessages &&
      attachFilesMessages.userMessage &&
      attachFilesMessages.aiMessage
    ) {
      setMessages((prev) => [
        ...prev,
        attachFilesMessages.userMessage,
        attachFilesMessages.aiMessage,
      ]);
    }
  }, [attachFilesMessages]);

  // Handle incomplete data message from ReviewMappingsModal
  // This adds a user message and sends it to the AI chat API
  useEffect(() => {
    if (!incompleteDataMessage || variant !== "order-details") return;

    // Prevent duplicate processing of the same message
    if (lastProcessedIncompleteDataMessageRef.current === incompleteDataMessage) {
      return;
    }

    // Require status fields for order chat API calls
    if (!orderStatusFields) {
      console.warn(
        "Order chat: Missing status fields for incomplete data message",
      );
      return;
    }

    // Mark this message as processed to prevent re-running
    lastProcessedIncompleteDataMessageRef.current = incompleteDataMessage;

    const timestamp = Date.now();
    const userMessage = {
      id: `m${timestamp}`,
      role: "user",
      text: incompleteDataMessage,
      name: "You",
      timestamp: timestamp,
    };

    // Add user message immediately
    setMessages((prev) => [...prev, userMessage]);

    // Send the message to AI chat API
    const sendIncompleteDataMessage = async () => {
      setIsAILoading(true);
      try {
        const response = await AIService.sendMessage({
          message: incompleteDataMessage,
          userId: userEmail,
          orderId: orderId,
          region: selectedRegion?.id || "general",
          ...(orderStatusFields && { statusFields: orderStatusFields }),
        });

        console.log(
          "[Chatbot] AI Query API Response (Incomplete Data):",
          response,
        );

        const aiTimestamp = Date.now();
        const aiMessage = {
          id: `m${aiTimestamp}`,
          role: "ai",
          type: "aiCard",
          text:
            response?.response ||
            response?.message ||
            "Status update request received.",
          header: { title: "GDS.ai Axon" },
          timestamp: aiTimestamp,
        };

        setMessages((prev) => [...prev, aiMessage]);

        // Refresh order details after status update
        if (onRefreshOrderDetails) {
          onRefreshOrderDetails();
        }
      } catch (error) {
        // Check if request was aborted via kill switch - don't show error message
        if (error.name === "AbortError" || error?.aborted) {
          console.log(
            "[Chatbot] Request was aborted, not showing error message",
          );
          return;
        }

        console.error("Failed to send incomplete data message:", error);
        const errorTimestamp = Date.now();
        const errorMessage = {
          id: `m${errorTimestamp}`,
          role: "ai",
          type: "aiCard",
          text: "Sorry, I encountered an error processing your request. Please try again.",
          header: { title: "GDS.ai Axon" },
          timestamp: errorTimestamp,
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsAILoading(false);
      }
    };

    sendIncompleteDataMessage();
  }, [
    incompleteDataMessage,
    variant,
    orderId,
    userEmail,
    onRefreshOrderDetails,
    selectedRegion?.id,
    orderStatusFields,
  ]);

  // Update the AI summary message when activeSummaries (openOrdersSummaries or actionRequiredLogs) becomes available
  // When opening from notification: only fetch recommended actions from logs API (email/action summaries come from notification)
  useEffect(() => {
    const isOpenOrder = source === "openOrders";

    if (
      variant !== "order-details" ||
      !orderId ||
      !activeSummaries ||
      activeSummaries.length === 0
    ) {
      return;
    }

    const {
      emailSummaries,
      recommendedActions: parsedActions,
      actionTakenSummary,
      actionTakenList,
    } = parseOrderContextFromLogs(activeSummaries);

    // Store recommended actions in refs - always from logs API
    parsedRecommendedActionsRef.current = parsedActions || [];

    // Update email/action summaries from logs ONLY if NOT from notification
    // For all notification types (including Action Needed), email/action summaries come from notification data
    if (!fromNotification) {
      parsedEmailSummariesRef.current = emailSummaries;
      parsedActionTakenSummaryRef.current = actionTakenSummary;
      parsedActionTakenListRef.current = actionTakenList;
    }

    // Update existing AI summary message with email summaries and actions
    // or add a new message if no summary message exists yet
    setMessages((prev) => {
      const hasSummaryMessage = prev.some((msg) =>
        msg.id?.startsWith("m_summary_"),
      );
      //start stop Refresh previous AI card Recommended Actions 1st
      const firstSummaryIndex = messages.findIndex((msg) =>
        msg.id?.startsWith("m_summary_"),
      );
      const latestRecommendedActionCardIndex = messages.findIndex(
        (msg) => msg.id === latestRecommendedActionCardId,
      );
      const hasCurrentNonSummaryRecommendedActionCard =
        latestRecommendedActionCardId &&
        !latestRecommendedActionCardId.startsWith("m_summary_") &&
        firstSummaryIndex >= 0 &&
        latestRecommendedActionCardIndex > firstSummaryIndex;
      const shouldRefreshSummaryRecommendedActions =
        !hasCurrentNonSummaryRecommendedActionCard &&
        !hasSeenNonSummaryRecommendedActionCardRef.current &&
        (!latestRecommendedActionCardId ||
          latestRecommendedActionCardId.startsWith("m_summary_"));
      //end stop Refresh previous AI card Recommended Actions 1st

      if (hasSummaryMessage) {
        // Update existing summary message
        return prev.map((msg) => {
          if (msg.id?.startsWith("m_summary_")) {
            // When from notification: only update recommended actions from logs API
            // email/action summaries are already set from notification details
            if (fromNotification) {
              return {
                ...msg,
                //start stop Refresh previous AI card Recommended Actions 1st
                //  recommendedActions: parsedActions ? parsedActions : msg.recommendedActions,
                recommendedActions: shouldRefreshSummaryRecommendedActions
                  ? parsedActions
                    ? parsedActions
                    : msg.recommendedActions
                  : msg.recommendedActions,
                //end stop Refresh previous AI card Recommended Actions 1st

                //showRecommendedActions: true, //stop Refresh previous AI card Recommended Actions 1st
                //start stop Refresh previous AI card Recommended Actions 1st
                showRecommendedActions: shouldRefreshSummaryRecommendedActions
                  ? true
                  : msg.showRecommendedActions,
                //end stop Refresh previous AI card Recommended Actions 1st
              };
            }
            // Normal flow (not from notification): update all fields from logs API
            return {
              ...msg,
              isInitialOrderDetailsResponse: true,
              emailSummaries: emailSummaries,
              actionTakenSummary: actionTakenSummary,
              actionTakenList: actionTakenList,
              //start stop Refresh previous AI card Recommended Actions 1st
              //recommendedActions: parsedActions ? parsedActions : msg.recommendedActions,
              recommendedActions: shouldRefreshSummaryRecommendedActions
                ? parsedActions
                  ? parsedActions
                  : msg.recommendedActions
                : msg.recommendedActions,
              //end stop Refresh previous AI card Recommended Actions 1st

              //showRecommendedActions: true, // stop Refresh previous AI card Recommended Actions 1st
              //start stop Refresh previous AI card Recommended Actions 1st
              showRecommendedActions: shouldRefreshSummaryRecommendedActions
                ? true
                : msg.showRecommendedActions,
              //end stop Refresh previous AI card Recommended Actions 1st
              source: source,
            };
          }
          return msg;
        });
      } else {
        // Don't create a new message - wait for AI response to be fetched first
        // The email summaries and recommended actions are stored in refs and will be included when AI response is created
        return prev;
      }
    });
  }, [
    variant,
    orderId,
    source,
    activeSummaries,
    latestRecommendedActionCardId, //stop Refresh previous AI card Recommended Actions 1st
    orderChanges,
    location?.state?.notificationCategory,
    notificationNavigationId,
    parseOrderContextFromLogs,
    fromNotification,
  ]);

  // Handles text input changes
  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  // Handles enter key to send message
  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onSend();
    }
  };

  // Opens suggestions dialog
  const handleSeeAllClick = () => {
    setShowDialog(true);
    setActiveTab(CHAT.AI_SUGGESTIONS);
  };

  // Closes suggestions dialog
  const handleCloseDialog = () => {
    setShowDialog(false);
  };

  // Switches active suggestion tab
  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
  };

  // Handles generic action selection from suggestions
  const getChatModeForSuggestion = (text) => {
    const normalized = String(text || "").toLowerCase();

    if (normalized.includes("data triage")) {
      return "ordersInDataTriage";
    }

    if (normalized.includes("newly allocated")) {
      return "newlyAllocatedOrders";
    }

    if (normalized.includes("high priority")) {
      return "highPriorityOrders";
    }

    return "planMyDay";
  };

  const handleGenericAction = (text) => {
    setShowDialog(false);

    // Check if this suggestion has a direct static_view_type mapping
    // Pass selectedRole for role-dependent mappings like "my weekly orders"
    const staticViewType = getStaticViewTypeFromText(text, selectedRole);

    if (variant === "order-details") {
      navigate("/home", {
        state: {
          chatMode: getChatModeForSuggestion(text),
          initialMessage: text,
          staticViewType: staticViewType,
        },
      });
      return;
    }

    if (variant === "home" && onSendToSuperAgent) {
      // Pass both text and staticViewType to SuperAgentChat
      onSendToSuperAgent(text, staticViewType);
      return;
    }

    // Fallback for non-home variant: Add user message
    const userMsg = {
      id: `m${Date.now()}`,
      role: "user",
      text: text,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, userMsg]);

    // Add loading message
    setTimeout(() => {
      const loadingMessage = {
        id: `loading-${Date.now()}`,
        role: "ai",
        type: "aiCard",
        loading: true,
        loadingText: "Processing your request...",
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, loadingMessage]);

      // Replace loading with generic AI response after a delay
      setTimeout(() => {
        setMessages((prev) => {
          const withoutLoading = prev.filter((m) => m.id !== loadingMessage.id);
          const aiResponse = {
            id: `m${Date.now()}`,
            role: "ai",
            type: "genericSuper",
            text: `I understand you're asking about "${text}". This is a generic response. I'm here to help you with your queries.`,
            header: { title: "GDS.ai Axon" },
            timestamp: Date.now(),
          };
          return [...withoutLoading, aiResponse];
        });
      }, 1500);
    }, 300);
  };

  // Define suggestions for each tab with navigation handling
  const handlePlanMyDayWithNav = () => {
    if (path === "/" || path === "/home") {
      onPlanMyDay?.();
    } else {
      navigate("/home", { state: { chatMode: "planMyDay" } });
    }
  };

  const handleOrdersInDataTriageWithNav = () => {
    if (path === "/" || path === "/home") {
      onOrdersInDataTriage?.();
    } else {
      navigate("/home", { state: { chatMode: "ordersInDataTriage" } });
    }
  };

  const handleNewlyAllocatedOrdersWithNav = () => {
    if (path === "/" || path === "/home") {
      onNewlyAllocatedOrders?.();
    } else {
      navigate("/home", { state: { chatMode: "newlyAllocatedOrders" } });
    }
  };

  // Legacy suggestions (for backward compatibility)
  const suggestionsByTab = getSuggestionsByTab({
    onPlanMyDay: handlePlanMyDayWithNav,
    onOrdersInDataTriage: handleOrdersInDataTriageWithNav,
    onNewlyAllocatedOrders: handleNewlyAllocatedOrdersWithNav,
    onGenericAction: handleGenericAction,
  });

  // Role-based inline suggestions (3 buttons + See All)
  const roleBasedInlineSuggestions = getRoleBasedInlineSuggestions({
    onPlanMyDay: handlePlanMyDayWithNav,
    onOrdersInDataTriage: handleOrdersInDataTriageWithNav,
    onNewlyAllocatedOrders: handleNewlyAllocatedOrdersWithNav,
    onGenericAction: handleGenericAction,
    selectedRole,
  });

  // Role-based popup suggestions (for See All modal)
  const roleBasedPopupSuggestions = getRoleBasedPopupSuggestions({
    onPlanMyDay: handlePlanMyDayWithNav,
    onOrdersInDataTriage: handleOrdersInDataTriageWithNav,
    onNewlyAllocatedOrders: handleNewlyAllocatedOrdersWithNav,
    onGenericAction: handleGenericAction,
    selectedRole,
  });

  const currentSuggestions =
    roleBasedPopupSuggestions[activeTab] || suggestionsByTab[activeTab] || [];

  // Hide the legacy "Most used" popup section and show only the supported tabs.
  const availableTabs = useMemo(() => {
    //  const roleName =
    //   typeof selectedRole === "object"
    //     ? (selectedRole?.roleName || selectedRole?.name || "").toUpperCase()
    //     : String(selectedRole || "").toUpperCase();

    return [
      CHAT.AI_SUGGESTIONS,
      // CHAT.KNOWLEDGE_PROMPTS, // Hidden for now; keep code for future enablement.
    ];
    // }
  }, []);

  const refreshOrderContextLogs = useCallback(async () => {
    if (variant !== "order-details" || !orderAxonId) {
      return;
    }

    const refreshAction =
      source === "openOrders"
        ? fetchOpenOrdersSummaries({ axonId: orderAxonId, force: true })
        : fetchActionRequiredLogs({ axonId: orderAxonId, force: true });

    try {
      // await dispatch(refreshAction).unwrap();
      const result = await dispatch(refreshAction).unwrap();
      return result;
    } catch (error) {
      console.error("Failed to refresh order logs:", error);
    }
  }, [dispatch, orderAxonId, source, variant]);

  // Start  BUG-465083
  const refreshOrderStatusAfterActionTaken = useCallback(async () => {
    if (variant !== "order-details" || !orderAxonId) {
      return { source };
    }

    let actionNeededResult;

    try {
      actionNeededResult = await dispatch(
        fetchActionRequiredLogs({ axonId: orderAxonId, force: true }),
      ).unwrap();
    } catch (error) {
      console.error("Failed to refresh action needed status:", error);

      return {
        source,
        openOrdersEntries: [],
        actionNeededEntries: [],
      };
    }

    const actionNeededEntries = normalizeOrderStatusEntries(actionNeededResult);
    const isActionRequired = getIsActionRequiredFromPayload(actionNeededResult);

    if (isActionRequired === true) {
      return {
        source: "actionNeeded",
        openOrdersEntries: [],
        actionNeededEntries,
      };
    }

    if (isActionRequired !== false) {
      return {
        source,
        openOrdersEntries: [],
        actionNeededEntries,
      };
    }

    let openOrdersEntries = [];

    try {
      const openOrdersResult = await dispatch(
        fetchOpenOrdersSummaries({ axonId: orderAxonId, force: true }),
      ).unwrap();
      openOrdersEntries = normalizeOrderStatusEntries(openOrdersResult);
    } catch (error) {
      console.error("Failed to refresh open order status:", error);
    }

    return {
      source: "openOrders",
      openOrdersEntries,
      actionNeededEntries,
    };
  }, [dispatch, orderAxonId, source, variant]);
  // End  BUG-465083

  // Ref to track streaming message for word-by-word updates
  const streamingMessageIdRef = useRef(null);
  // Ref to track animation timeout for cleanup
  const animationTimeoutRef = useRef(null);

  // Word-by-word animation function
  const animateTextWordByWord = useCallback((fullText, messageId) => {
    // Clear any existing animation
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    const words = fullText.split(/(\s+)/); // Split keeping whitespace
    let currentIndex = 0;
    let displayedText = "";

    const animateNextWord = () => {
      if (currentIndex < words.length) {
        displayedText += words[currentIndex];
        currentIndex++;

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId ? { ...msg, text: displayedText } : msg,
          ),
        );

        // Continue animation with 20ms delay per word
        animationTimeoutRef.current = setTimeout(animateNextWord, 20);
      }
    };

    // Start animation immediately
    animateNextWord();
  }, []);

  const sendMessageToAI = useCallback(
    async (messageText, options = {}) => {
      // const { hideStateUpdateButtons = false } = options;
      const { hideStateUpdateButtons = false, showRecommendedActions = false } =
        options;

      // For order chat (order-details), require status fields before calling query API
      if (variant === "order-details" && !orderStatusFields) {
        console.warn(
          "Order chat: Missing status fields, cannot call query API",
        );
        setIsAILoading(false);
        return;
      }

      // Show loading state - will switch to streaming message when first chunk arrives
      setIsAILoading(true);

      // Prepare streaming message ID
      const streamingMessageId = `m_streaming_${Date.now()}`;
      streamingMessageIdRef.current = streamingMessageId;
      let streamingMessageCreated = false;

      // Track flags received during streaming - use object reference that persists across closure
      const streamedFlags = {
        ui_fields_flag: undefined,
        ui_fields: undefined,
        checkpoint_pending: undefined,
        checkpoint_pending_agent: undefined,
        efforts_flag: undefined,
        savings_flag: undefined,
        update: undefined,
        refresh_flag: undefined,
        thread_id: undefined,
        turn_id: undefined,
        is_static_view: undefined,
      };

      try {
        let streamedText = "";

        const response = await AIService.sendMessage({
          message: messageText,
          threadId: threadIdRef.current,
          userId: userEmail,
          orderId: orderId,
          region: selectedRegion?.id || "general",
          ...(orderStatusFields && { statusFields: orderStatusFields }),

          // Handle streaming events
          onChunk: ({ eventType, data }) => {
            // Capture any flags from any event type
            if (data) {
              if (data.ui_fields_flag !== undefined)
                streamedFlags.ui_fields_flag = data.ui_fields_flag;
              if (data.ui_fields !== undefined)
                streamedFlags.ui_fields = data.ui_fields;
              if (data.checkpoint_pending !== undefined)
                streamedFlags.checkpoint_pending = data.checkpoint_pending;
              if (data.checkpoint_pending_agent !== undefined)
                streamedFlags.checkpoint_pending_agent = data.checkpoint_pending_agent;
              if (data.efforts_flag !== undefined)
                streamedFlags.efforts_flag = data.efforts_flag;
              if (data.savings_flag !== undefined)
                streamedFlags.savings_flag = data.savings_flag;
              if (data.update !== undefined) streamedFlags.update = data.update;
              if (data.refresh_flag !== undefined)
                streamedFlags.refresh_flag = data.refresh_flag;
              if (data.thread_id !== undefined)
                streamedFlags.thread_id = data.thread_id;
              if (data.turn_id !== undefined)
                streamedFlags.turn_id = data.turn_id;
              if (data.is_static_view !== undefined)
                streamedFlags.is_static_view = data.is_static_view;

              // Update streaming message with thread_id and turn_id as soon as they arrive
              if (data.thread_id || data.turn_id) {
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === streamingMessageId
                      ? {
                          ...msg,
                          ...(data.thread_id && { thread_id: data.thread_id }),
                          ...(data.turn_id && { turn_id: data.turn_id }),
                        }
                      : msg,
                  ),
                );
              }

              console.log(
                "[Chatbot] Captured streamedFlags:",
                JSON.stringify(streamedFlags),
              );
            }

            // Handle both "review_complete" and "result" event types (API may send either)
            if (
              (eventType === "review_complete" || eventType === "result") &&
              data
            ) {
              const responseText =
                data.response || data.text || data.message || "";
              if (responseText) {
                streamedText = responseText;

                // Turn off loading and create streaming message on FIRST chunk
                if (!streamingMessageCreated) {
                  setIsAILoading(false);
                  const streamingMessage = {
                    id: streamingMessageId,
                    role: "ai",
                    type: "aiCard",
                    header: { title: "GDS.ai Axon" },
                    text: "",
                    isStreaming: true,
                    source: source,
                    timestamp: Date.now(),
                  };
                  streamingMessageCreated = true;
                  setMessages((prev) => {
                    // Use setTimeout to ensure state update completes before animation starts
                    setTimeout(() => {
                      animateTextWordByWord(responseText, streamingMessageId);
                    }, 0);
                    return [...prev, streamingMessage];
                  });
                } else {
                  // Message already exists, just animate
                  animateTextWordByWord(responseText, streamingMessageId);
                }
              }
            } else if (eventType === "images" && data) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === streamingMessageId
                    ? {
                        ...msg,
                        retrieved_images: data.retrieved_images || [],
                        image_scores: data.image_scores || [],
                      }
                    : msg,
                ),
              );
            } else if (eventType === "suggestions" && data) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === streamingMessageId
                    ? {
                        ...msg,
                        suggested_questions: data.suggested_questions || [],
                      }
                    : msg,
                ),
              );
            }
          },
        });

        console.log(
          "[Chatbot] AI Query API Response (sendMessageToAI):",
          response,
        );

        // Merge streamedFlags into response for consistent flag access
        // Filter out undefined values from streamedFlags before merging
        const cleanedStreamedFlags = Object.fromEntries(
          Object.entries(streamedFlags).filter(([_, v]) => v !== undefined),
        );
        const mergedResponse = { ...response, ...cleanedStreamedFlags };

        console.log("[Chatbot] Final response:", response);
        console.log("[Chatbot] StreamedFlags:", streamedFlags);
        console.log("[Chatbot] CleanedStreamedFlags:", cleanedStreamedFlags);
        console.log("[Chatbot] MergedResponse:", mergedResponse);
        console.log(
          "[Chatbot] update flag:",
          mergedResponse?.update,
          "| refresh_flag:",
          mergedResponse?.refresh_flag,
        );

        // If no streaming message was created (no review_complete received), create final message
        if (!streamingMessageCreated) {
          setIsAILoading(false);

          // Check if we should refresh order details (even without streaming)
          const shouldRefreshNonStreaming =
            (mergedResponse?.update === true ||
              mergedResponse?.refresh_flag === true) &&
            onRefreshOrderDetails;

          console.log(
            "[Chatbot] Non-streaming path - shouldRefresh:",
            shouldRefreshNonStreaming,
            "| update:",
            mergedResponse?.update,
            "| refresh_flag:",
            mergedResponse?.refresh_flag,
          );

          if (shouldRefreshNonStreaming) {
            console.log(
              "[Chatbot] Calling onRefreshOrderDetails from non-streaming path...",
            );
            onRefreshOrderDetails();
          }

          // Also refresh order context logs if needed
          if (
            variant === "order-details" &&
            Boolean(orderAxonId) &&
            (mergedResponse?.update === true ||
              mergedResponse?.refresh_flag === true)
          ) {
            //start stop Refresh previous AI card Recommended Actions 2nd
            freezeInitialSummaryRecommendedActions();
            //end stop Refresh previous AI card Recommended Actions 2nd
            refreshOrderContextLogs();
          }

          const finalText =
            mergedResponse?.response ||
            mergedResponse?.message ||
            mergedResponse?.answer ||
            `I understand you're asking about "${messageText}". How can I assist you further?`;
          const aiResponse = {
            id: streamingMessageId,
            role: "ai",
            type: "aiCard",
            header: { title: "GDS.ai Axon" },
            text: finalText,
            isStreaming: false,
            source: source,
            timestamp: Date.now(),
            emailSummaries: [],
            recommendedActions:
              showRecommendedActions || mergedResponse?.update === true
                ? parsedRecommendedActionsRef.current
                : [],
            actionTakenSummary: null,
            actionTakenList: [],
            showRecommendedActions:
              showRecommendedActions || mergedResponse?.update === true,
            retrieved_images: mergedResponse?.retrieved_images || [],
            suggested_questions: mergedResponse?.suggested_questions || [],
            thread_id: mergedResponse?.thread_id || streamedFlags.thread_id,
            turn_id: mergedResponse?.turn_id || streamedFlags.turn_id,
            ...(hideStateUpdateButtons && { hideStateUpdateButtons: true }),
            ...((mergedResponse?.ui_fields_flag ||
              mergedResponse?.efforts_flag ||
              mergedResponse?.savings_flag) && {
              ui_fields_flag: true,
              ui_fields: mergedResponse.ui_fields || [],
              ...(mergedResponse?.efforts_flag === true && {
                efforts_flag: true,
              }),
              ...(mergedResponse?.savings_flag === true && {
                savings_flag: true,
              }),
            }),
            ...(mergedResponse?.checkpoint_pending === true && {
              checkpoint_pending: true,
              checkpoint_pending_agent:
                mergedResponse?.checkpoint_pending_agent ||
                streamedFlags.checkpoint_pending_agent ||
                "",
            }),
            // Pass is_static_view flag for Super Agent redirect
            ...((mergedResponse?.is_static_view === true || streamedFlags.is_static_view === true) && {
              is_static_view: true,
            }),
          };
          setMessages((prev) => [...prev, aiResponse]);
          refreshTrailLogs();
          return;
        }

        const shouldRefreshOrderLogs =
          variant === "order-details" &&
          Boolean(orderAxonId) &&
          (showRecommendedActions ||
            mergedResponse?.update === true ||
            mergedResponse?.refresh_flag === true);

        console.log(
          "[Chatbot] shouldRefreshOrderLogs:",
          shouldRefreshOrderLogs,
          "| variant:",
          variant,
          "| orderAxonId:",
          orderAxonId,
          "| showRecommendedActions:",
          showRecommendedActions,
          "| update:",
          mergedResponse?.update,
          "| refresh_flag:",
          mergedResponse?.refresh_flag,
        );
        //start stop Refresh previous AI card Recommended Actions 2nd
        if (shouldRefreshOrderLogs) {
          freezeInitialSummaryRecommendedActions();
        }
        //end stop Refresh previous AI card Recommended Actions 2nd

        const refreshedOrderLogs = shouldRefreshOrderLogs
          ? await refreshOrderContextLogs()
          : null;

        // Keep the follow-up AI response in sync with the freshly fetched order log context.
        const latestOrderContext = parseOrderContextFromLogs(
          refreshedOrderLogs || activeSummaries,
        );
        parsedEmailSummariesRef.current = latestOrderContext.emailSummaries;
        parsedRecommendedActionsRef.current =
          latestOrderContext.recommendedActions || [];
        parsedActionTakenSummaryRef.current =
          latestOrderContext.actionTakenSummary;
        parsedActionTakenListRef.current = latestOrderContext.actionTakenList;

        // Check if response has update: true or refresh_flag: true and refresh order details
        const shouldRefreshDetails =
          (mergedResponse?.update === true ||
            mergedResponse?.refresh_flag === true) &&
          onRefreshOrderDetails;
        console.log(
          "[Chatbot] shouldRefreshDetails:",
          shouldRefreshDetails,
          "| onRefreshOrderDetails exists:",
          !!onRefreshOrderDetails,
        );

        if (shouldRefreshDetails) {
          console.log("[Chatbot] Calling onRefreshOrderDetails...");
          onRefreshOrderDetails();
        }

        const shouldShowRecommendedActions =
          showRecommendedActions || mergedResponse?.update === true;

        // Finalize the streaming message with complete data
        const finalText =
          streamedText ||
          mergedResponse?.response ||
          mergedResponse?.message ||
          mergedResponse?.answer ||
          `I understand you're asking about "${messageText}". How can I assist you further?`;

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === streamingMessageId
              ? {
                  ...msg,
                  text: finalText,
                  isStreaming: false,
                  emailSummaries: [],
                  recommendedActions: shouldShowRecommendedActions
                    ? parsedRecommendedActionsRef.current
                    : [],
                  actionTakenSummary: null,
                  actionTakenList: [],
                  showRecommendedActions: shouldShowRecommendedActions,
                  retrieved_images:
                    mergedResponse?.retrieved_images ||
                    msg.retrieved_images ||
                    [],
                  suggested_questions:
                    mergedResponse?.suggested_questions ||
                    msg.suggested_questions ||
                    [],
                  thread_id:
                    mergedResponse?.thread_id || streamedFlags.thread_id,
                  turn_id: mergedResponse?.turn_id || streamedFlags.turn_id,
                  ...(hideStateUpdateButtons && {
                    hideStateUpdateButtons: true,
                  }),
                  ...((mergedResponse?.ui_fields_flag ||
                    mergedResponse?.efforts_flag ||
                    mergedResponse?.savings_flag) && {
                    ui_fields_flag: true,
                    ui_fields: mergedResponse.ui_fields || [],
                    ...(mergedResponse?.efforts_flag === true && {
                      efforts_flag: true,
                    }),
                    ...(mergedResponse?.savings_flag === true && {
                      savings_flag: true,
                    }),
                  }),
                  ...(mergedResponse?.checkpoint_pending === true && {
                    checkpoint_pending: true,
                    checkpoint_pending_agent:
                      mergedResponse?.checkpoint_pending_agent ||
                      streamedFlags.checkpoint_pending_agent ||
                      "",
                  }),
                  // Pass is_static_view flag for Super Agent redirect
                  ...((mergedResponse?.is_static_view === true || streamedFlags.is_static_view === true) && {
                    is_static_view: true,
                  }),
                }
              : msg,
          ),
        );

        // Refresh trail logs after AI response
        refreshTrailLogs();
      } catch (error) {
        // Check if request was aborted via kill switch - don't show error message
        if (error.name === "AbortError" || error?.aborted) {
          console.log(
            "[Chatbot] Request was aborted, not showing error message",
          );
          setIsAILoading(false);
          // Remove any streaming message that was created
          if (streamingMessageCreated) {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === streamingMessageId
                  ? { ...msg, isStreaming: false }
                  : msg,
              ),
            );
          }
          return;
        }

        console.error("Failed to fetch AI response:", error);
        setIsAILoading(false);

        // If streaming message was created, update it with error
        if (streamingMessageCreated) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === streamingMessageId
                ? {
                    ...msg,
                    text: `Sorry, I encountered an error processing your request. Please try again.`,
                    isStreaming: false,
                  }
                : msg,
            ),
          );
        } else {
          // If no streaming message yet, create error message
          const aiResponse = {
            id: streamingMessageId,
            role: "ai",
            type: "aiCard",
            header: { title: "GDS.ai Axon" },
            text: `Sorry, I encountered an error processing your request. Please try again.`,
            source: source,
            timestamp: Date.now(),
          };
          setMessages((prev) => [...prev, aiResponse]);
        }
      } finally {
        streamingMessageIdRef.current = null;
      }
    },
    [
      // [onRefreshOrderDetails, orderId, source, userEmail],
      onRefreshOrderDetails,
      activeSummaries,
      animateTextWordByWord,
      orderAxonId,
      orderId,
      orderStatusFields,
      parseOrderContextFromLogs,
      refreshOrderContextLogs,
      refreshTrailLogs,
      selectedRegion?.id,
      source,
      userEmail,
      variant,
    ],
  );

  // Fetches activity log from API and displays in chat
  const fetchActivityLog = useCallback(async () => {
    // Extract axonId from orderDetails for the activitylog API endpoint
    const axonId = Array.isArray(orderDetails)
      ? orderDetails[0]?.headerSection?.[0]?.axonId || orderId
      : orderDetails?.headerSection?.[0]?.axonId || orderId;

    if (!axonId) {
      console.error("Order ID (axonId) is required to fetch activity log");
      return;
    }

    const loadingMessage = {
      id: `loading-${Date.now()}`,
      role: "ai",
      type: "aiCard",
      loading: true,
      loadingText: "Fetching Activity Log...",
      header: { title: CHAT.AXON_AI },
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, loadingMessage]);

    try {
      const response = await OrdersService.getActivityLog(axonId);

      // Parse the activity logs from the response
      // Handle new API structure: { success, message, data: { activityLogs, page, pageSize, totalCount }, errors }
      const activityLogs = Array.isArray(response)
        ? response
        : response?.data?.activityLogs ||
          response?.activityLogs ||
          response?.data ||
          [];

      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== loadingMessage.id);
        const aiResponse = {
          id: `m${Date.now()}`,
          role: "ai",
          type: "aiCard",
          activityLog: true,
          activityLogs: activityLogs,
          header: { title: CHAT.AXON_AI },
          timestamp: Date.now(),
        };
        return [...filtered, aiResponse];
      });
    } catch (error) {
      console.error("Failed to fetch activity log:", error);
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== loadingMessage.id);
        const aiResponse = {
          id: `m${Date.now()}`,
          role: "ai",
          type: "aiCard",
          text: "Failed to fetch activity log. Please try again.",
          header: { title: CHAT.AXON_AI },
          timestamp: Date.now(),
        };
        return [...filtered, aiResponse];
      });
    }
  }, [orderId, orderDetails]);

  const handleFieldsUpdatePromptInputChange = (messageId, value) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId ? { ...msg, draftValue: value } : msg,
      ),
    );
  };

  const handleFieldsUpdatePromptSend = (message) => {
    const inputValue = String(message?.draftValue || "").trim();
    if (!inputValue) {
      return;
    }

    const destination = String(message?.to || "").trim();
    const composedText = destination
      ? `Update ${destination} to ${inputValue}`
      : inputValue;

    // Transform the inline prompt card into a regular sent user message.
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === message.id
          ? {
              ...msg,
              type: "sentPromptMessage",
              role: "user",
              name: "You",
              text: composedText,
            }
          : msg,
      ),
    );

    // sendMessageToAI(composedText);
    sendMessageToAI(composedText, { showRecommendedActions: true });

    if (onActionClick) {
      onActionClick(composedText);
    }
  };

  // Handle stop response - abort the current AI request
  const handleStopResponse = useCallback(() => {
    // Abort the current AI request
    abortCurrentRequest();
    // Clear any animation in progress
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }
    // Stop loading state
    setIsAILoading(false);
    // Mark any streaming messages as complete (don't add error messages)
    setMessages((prev) =>
      prev.map((msg) =>
        msg.isStreaming ? { ...msg, isStreaming: false } : msg,
      ),
    );
  }, []);

  const onSend = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    // Mark that user has interacted with this order
    setHasUserInteracted(true);

    // If in home variant and onSendToSuperAgent is provided, send to SuperAgentChat
    if (variant === "home" && onSendToSuperAgent) {
      onSendToSuperAgent(trimmed);
      setInputValue("");
      return;
    }

    const newMsg = {
      id: `m${Date.now()}`,
      role: "user",
      text: trimmed,
      name: "You",
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, newMsg]);
    setInputValue("");

    // Send all user messages to the AI API
    // Previously there was field matching logic here that showed static cards
    // for field names like "Preparer", "Reviewer", etc. - removed to always use AI API
    sendMessageToAI(trimmed);
  };

  const handleActionClick = async (action) => {
    // Mark that user has interacted with this order
    setHasUserInteracted(true);

    const actionData =
      typeof action === "object" && action.action ? action : null;
    const isEffortsSavingsQueryAction =
      actionData?.action === "showEffortsSavingsForm" && actionData?.type;
    const actionType =
      typeof action === "object" && action?.type ? String(action.type) : "";
    // const actionText = actionData
    const actionText = isEffortsSavingsQueryAction
      ? actionData.displayText ||
        (actionData.type === "efforts" ? "Update Efforts" : "Update Savings")
      : actionData
        ? actionData.action
        : typeof action === "string"
          ? action
          : action.label || action;

    if (
      actionType === "frontendAttachFile" ||
      actionText === "Attach Files" ||
      actionText === "Attach File"
    ) {
      const attachFileText = "Attach Files";
      const timestamp = Date.now();

      // Create user message
      const userMsg = {
        id: `m${timestamp}`,
        role: "user",
        text: attachFileText,
        name: "You",
        timestamp: timestamp,
      };

      // Create AI response with AttachFilesAICard
      const aiMsg = {
        id: `m${timestamp + 1}`,
        role: "ai",
        type: "aiCard",
        attachFilesCard: true,
        text: `Please attach following files from the left panel by clicking "Attach files" button

▸ Journal Entry (JE) Files (Mandatory)
▸ Trial Balance (TB) Files (Mandatory)
▸ Chart of Account (COA) Files (Recommended)

Before you attach..

▸ Ensure files are unzipped.
▸ Upload files in the correct folders
▸ Confirm all mandatory files are placed in the mandatory (CY) folder

Once you're done, just let me know—I'll take it from there and start pre-validating the files for you.`,
        header: { title: CHAT.AXON_AI },
        timestamp: timestamp + 1,
      };

      // Add both messages
      setMessages((prev) => [...prev, userMsg, aiMsg]);

      // Trigger Files tab switch in left section
      if (onActionClick) {
        onActionClick({
          type: "frontendAttachFile",
          action: attachFileText,
        });
      }
      return;
    }
    //    // Handle showEffortsSavingsForm action - display user message and render form
    // // without calling the AI API
    // if (actionText === "showEffortsSavingsForm" && actionData?.type) {
    //   const displayText =
    //     actionData.displayText ||
    //     (actionData.type === "efforts" ? "Update Efforts" : "Update Savings");

    //   // Add user message
    //   const userMsg = {
    //     id: `m${Date.now()}`,
    //     role: "user",
    //     text: displayText,
    //     name: "You",
    //     timestamp: Date.now(),
    //   };
    //   setMessages((prev) => [...prev, userMsg]);

    // Add AI response with the for
    //  // Add AI response with the form
    //   const aiMsg = {
    //     id: `m${Date.now() + 1}`,
    //     role: "ai",
    //     type: "aiCard",
    //     header: { title: CHAT.AXON_AI },
    //     effortsSavingsForm: true,
    //     effortsSavingsType: actionData.type, // 'efforts' or 'savings'
    //     source: source,
    //     timestamp: Date.now() + 1,
    //   };
    //   setMessages((prev) => [...prev, aiMsg]);
    //   return;
    // }

    // Handle resumeCheckpoint action - send resume_input to the AI service
    // instead of calling the update-order API. Triggered when a human-in-the-loop
    // checkpoint is pending and the user clicks the Confirm button on the card.
    if (actionText === "resumeCheckpoint" && actionData?.resume_input) {
      const resumeInput = actionData.resume_input;
      // Find the most recent user query from the chat history so we can re-send
      // it alongside resume_input (the backend expects the original query, not an
      // empty string, to resume the workflow).
      const previousUserQuery = (() => {
        for (let i = messages.length - 1; i >= 0; i -= 1) {
          const m = messages[i];
          if (
            m?.role === "user" &&
            typeof m.text === "string" &&
            m.text.trim()
          ) {
            return m.text.trim();
          }
        }
        return "";
      })();

      // Optionally render a user bubble summarizing the confirmed values
      //  const summaryText = Object.entries(resumeInput)
      const summaryText =
        typeof actionData?.resume_input_summary_text === "string" &&
        actionData.resume_input_summary_text.trim()
          ? actionData.resume_input_summary_text.trim()
          : Object.entries(resumeInput)
              .map(([k, v]) => `${k}: ${v}`)
              .join(", ");
      if (summaryText) {
        const userMsg = {
          id: `m${Date.now()}`,
          role: "user",
          text: summaryText,
          name: "You",
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, userMsg]);
      }

      setIsAILoading(true);
      try {
        const response = await AIService.sendMessage({
          message: previousUserQuery,
          threadId: threadIdRef.current,
          userId: userEmail,
          orderId: orderId,
          region: selectedRegion?.id || "general",
          resumeInput,
          ...(orderStatusFields && { statusFields: orderStatusFields }),
        });

        console.log(
          "[Chatbot] AI Query API Response (Checkpoint Resume):",
          response,
        );

        if (
          (response?.update === true || response?.refresh_flag === true) &&
          onRefreshOrderDetails
        ) {
          onRefreshOrderDetails();
        }

        const aiResponse = {
          id: `m${Date.now()}`,
          role: "ai",
          type: "aiCard",
          header: { title: CHAT.AXON_AI },
          text:
            response?.response || response?.message || response?.answer || "",
          source: source,
          timestamp: Date.now(),
          ...((response?.ui_fields_flag ||
            response?.efforts_flag ||
            response?.savings_flag) && {
            ui_fields_flag: true,
            ui_fields: response.ui_fields || [],
            ...(response?.efforts_flag === true && { efforts_flag: true }),
            ...(response?.savings_flag === true && { savings_flag: true }),
          }),
          ...(response?.checkpoint_pending === true && {
            checkpoint_pending: true,
            checkpoint_pending_agent: response?.checkpoint_pending_agent || "",
          }),
        };
        setMessages((prev) => [...prev, aiResponse]);
        refreshTrailLogs();
      } catch (error) {
        // Check if request was aborted via kill switch - don't show error message
        if (error.name === "AbortError" || error?.aborted) {
          console.log(
            "[Chatbot] Request was aborted, not showing error message",
          );
          return;
        }

        console.error("Failed to resume checkpoint:", error);
        const aiResponse = {
          id: `m${Date.now()}`,
          role: "ai",
          type: "aiCard",
          header: { title: CHAT.AXON_AI },
          text: "Sorry, I could not confirm your changes. Please try again.",
          source: source,
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, aiResponse]);
      } finally {
        setIsAILoading(false);
      }
      return;
    }

    // Handle updateFieldsSuccess action - render response message in chat
    if (actionText === "updateFieldsSuccess" && actionData?.responseMessage) {
      // Refresh order details in left section (this also triggers trail logs refresh via trailRefreshKey)
      if (onRefreshOrderDetails) {
        onRefreshOrderDetails();
      }

      //start stop Refresh previous AI card Recommended Actions 2nd
      freezeInitialSummaryRecommendedActions();
      //end stop Refresh previous AI card Recommended Actions 2nd
      const refreshedOrderLogs = await refreshOrderContextLogs();
      const latestOrderContext = parseOrderContextFromLogs(
        refreshedOrderLogs || activeSummaries,
      );

      parsedEmailSummariesRef.current = latestOrderContext.emailSummaries;
      parsedRecommendedActionsRef.current =
        latestOrderContext.recommendedActions || [];
      parsedActionTakenSummaryRef.current =
        latestOrderContext.actionTakenSummary;
      parsedActionTakenListRef.current = latestOrderContext.actionTakenList;

      const aiResponse = {
        id: `m${Date.now()}`,
        role: "ai",
        type: "aiCard",
        text: actionData.responseMessage,
        //emailSummaries: latestOrderContext.emailSummaries,
        recommendedActions:
          latestOrderContext.recommendedActions ||
          parsedRecommendedActionsRef.current,
        // actionTakenSummary: latestOrderContext.actionTakenSummary,
        // actionTakenList: latestOrderContext.actionTakenList,
        showRecommendedActions: true,
        header: { title: CHAT.AXON_AI },
        source: source,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, aiResponse]);
      return;
    }

    // Handle updateFieldsFailed action - render error message in chat
    if (actionText === "updateFieldsFailed" && actionData?.error) {
      //start stop Refresh previous AI card Recommended Actions 2nd
      freezeInitialSummaryRecommendedActions();
      //end stop Refresh previous AI card Recommended Actions 2nd
      const refreshedOrderLogs = await refreshOrderContextLogs();
      const latestOrderContext = parseOrderContextFromLogs(
        refreshedOrderLogs || activeSummaries,
      );

      parsedEmailSummariesRef.current = latestOrderContext.emailSummaries;
      parsedRecommendedActionsRef.current =
        latestOrderContext.recommendedActions || [];
      parsedActionTakenSummaryRef.current =
        latestOrderContext.actionTakenSummary;
      parsedActionTakenListRef.current = latestOrderContext.actionTakenList;
      // AXON-CHAT-VALIDATION-WARNINGS: keep backend warning lists on the failed card instead of flattening them.
      const errorDetails = Array.isArray(actionData.error?.errors)
        ? actionData.error.errors
            .map((errorItem) => String(errorItem || "").trim())
            .filter(Boolean)
        : [];

      const aiResponse = {
        id: `m${Date.now()}`,
        role: "ai",
        type: "aiCard",
        updateFieldFailed: true,
        text:
          actionData.error?.message ||
          "Failed to update fields. Please try again.",
        errorMessage:
          actionData.error?.message ||
          "Failed to update fields. Please try again.",
        errorDetails,
        recommendedActions:
          latestOrderContext.recommendedActions ||
          parsedRecommendedActionsRef.current,
        showRecommendedActions: true,
        header: { title: CHAT.AXON_AI },
        source: source,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, aiResponse]);
      return;
    }

    // For field update required actions, send a direct user message.
    if (
      actionType === "fieldsUpdateRequired" ||
      actionType === "fields_update"
    ) {
      /* OLD VERSION - kept for reference, rendered AI prompt card first
      const promptMessage = {
        id: `m${Date.now()}`,
        role: "ai",
        type: "fieldsUpdatePrompt",
        to: actionText,
        draftValue: "",
        name: CHAT.AXON_AI,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, promptMessage]);
      return;
      */

      const fieldUpdateMessage = `Update ${actionText} for this order`;
      const userMsg = {
        id: `m${Date.now()}`,
        role: "user",
        text: fieldUpdateMessage,
        name: "You",
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, userMsg]);
      // sendMessageToAI(fieldUpdateMessage);
      sendMessageToAI(fieldUpdateMessage, { showRecommendedActions: true });
      if (onActionClick) {
        onActionClick(fieldUpdateMessage);
      }
      return;
    }

    // For state update actions, call order update API directly
    if (actionType === "stateUpdate" || actionType === "state_update") {
      const stateUpdateMsg = `Update Status to ${actionText}`;
      const userMsg = {
        id: `m${Date.now()}`,
        role: "user",
        text: stateUpdateMsg,
        name: "You",
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, userMsg]);

      // Get headerSection to extract requestId
      const headerSection = Array.isArray(orderDetails)
        ? orderDetails[0]?.headerSection?.[0]
        : orderDetails?.headerSection?.[0];

      // Get role name from selectedRole
      const currentUserRole =
        typeof selectedRole === "object"
          ? selectedRole?.roleName || selectedRole?.name || ""
          : String(selectedRole || "");

      // Prepare payload for order update API matching OrdersDetailsDashboard format
      const updatePayload = {
        requestId: headerSection?.requestId?.toString() || orderId || "0",
        lastModifiedEmployeeEmailID: userEmail,
        lastModifiedDate: new Date().toISOString(),
        currentStageId: 0,
        Role: currentUserRole,
        fields: [
          {
            FieldName: "StageName",
            UpdatedValue: actionText,
          },
        ],
        efforts: [],
        savings: [],
        agentType: "CHAT_AGENT",
      };

      try {
        setIsAILoading(true);
        //await OrdersService.updateOrder(updatePayload);
        const response = await OrdersService.updateOrder(updatePayload);
        const validationWarnings = getUpdateOrderValidationWarnings(response);

        // AXON-CHAT-VALIDATION-WARNINGS: a resolved response with warnings should render the failed card, not the success card.
        if (validationWarnings.length > 0) {
          freezeInitialSummaryRecommendedActions();
          const logsResult = await refreshOrderContextLogs();
          const parsedData = parseOrderContextFromLogs(logsResult);

          const warningMsg = {
            id: `m${Date.now()}`,
            role: "ai",
            type: "aiCard",
            updateFieldFailed: true,
            header: { title: "GDS.ai Axon" },
            text: "Validation warnings found.",
            errorMessage: "Validation warnings found.",
            errorDetails: validationWarnings,
            recommendedActions: parsedData?.recommendedActions || null,
            showRecommendedActions: true,
            source: source,
            timestamp: Date.now(),
          };
          setMessages((prev) => [...prev, warningMsg]);
          return;
        }

        // Refresh order details after status update
        if (typeof onRefreshOrderDetails === "function") {
          onRefreshOrderDetails();
        }

        // Fetch updated logs to get new recommended actions
        freezeInitialSummaryRecommendedActions();
        const logsResult = await refreshOrderContextLogs();
        const parsedData = parseOrderContextFromLogs(logsResult);

        // Create AI response message with GenericAIResponseCard format
        const successMsg = {
          id: `m${Date.now()}`,
          role: "ai",
          type: "aiCard",
          header: { title: "GDS.ai Axon" },
          text: `Status has been updated to "${actionText}" successfully.`,
          emailSummaries: parsedData?.emailSummaries || [],
          recommendedActions: parsedData?.recommendedActions || null,
          showRecommendedActions: true,
          actionTakenSummary: parsedData?.actionTakenSummary || null,
          actionTakenList: parsedData?.actionTakenList || [],
          source: source,
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, successMsg]);
      } catch (error) {
        console.error("Failed to update status:", error);
        const errorMsg = {
          id: `m${Date.now()}`,
          role: "ai",
          type: "aiCard",
          header: { title: "GDS.ai Axon" },
          text: `Failed to update status to "${actionText}". Please try again.`,
          source: source,
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, errorMsg]);
      } finally {
        setIsAILoading(false);
      }

      if (onActionClick) {
        onActionClick(stateUpdateMsg);
      }
      return;
    }

    if (
      actionText === "Action Taken" &&
      source === "actionNeeded" &&
      !actionData?.selectedFields
    ) {
      // Set the timestamp when Action Taken is clicked to disable previous cards' buttons
      const actionTakenTime = Date.now();
      setActionTakenTimestamp(actionTakenTime);

      const userMsg = {
        id: `m${actionTakenTime}`,
        role: "user",
        text: actionText,
        name: "You",
        timestamp: actionTakenTime,
      };
      setMessages((prev) => [...prev, userMsg]);

      try {
        const response = await UsersService.markNotificationActionTaken(
          "",
          orderId,
        );
        // Start  BUG-465083
        const refreshedOrderStatus = await refreshOrderStatusAfterActionTaken();
        const nextSource = refreshedOrderStatus?.source || source;
        if (nextSource !== source) {
          setSourceOverride(nextSource);
        }
        // End  BUG-465083

        const responseText = (() => {
          if (typeof response === "string") {
            return response;
          }

          return (
            response?.response ||
            response?.message ||
            response?.answer ||
            response?.result ||
            "Order marked as action taken successfully."
          );
        })();

        const aiResponse = {
          id: `m${Date.now()}`,
          role: "ai",
          type: "aiCard",
          header: { title: "GDS.ai Axon" },
          text: responseText,
          source: nextSource, //BUG-465083
          suppressRecommendedActions: true,
          timestamp: Date.now(),
        };
        setMessages((prev) => {
          const updatedMessages =
            nextSource === "openOrders"
              ? prev.map((message) => {
                  if (
                    message.role !== "ai" ||
                    message.type !== "aiCard" ||
                    message.timestamp >= actionTakenTime
                  ) {
                    return message;
                  }

                  return {
                    ...message,
                    source: nextSource,
                    suppressRecommendedActions: true,
                  };
                })
              : prev;

          return [...updatedMessages, aiResponse];
        });

        if (onRefreshOrderDetails) {
          onRefreshOrderDetails();
        }
      } catch (error) {
        console.error("Failed to mark order action taken:", error);
        const aiResponse = {
          id: `m${Date.now()}`,
          role: "ai",
          type: "aiCard",
          header: { title: "GDS.ai Axon" },
          text: "Failed to mark this order as action taken. Please try again.",
          source: source,
          suppressRecommendedActions: true,
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, aiResponse]);
      }

      if (onActionClick) {
        onActionClick(actionText);
      }
      return;
    }

    // If action is "showSingleFieldUpdate", directly render UpdateSingleFieldAICard for the single field
    // Handle this before the generic user message creation to use displayText
    if (actionData?.action === "showSingleFieldUpdate" && actionData?.field) {
      const primaryField = actionData.field;
      // Use fields array if provided (includes dependent fields like Other On Hold Reason),
      // otherwise fall back to single field
      const allFieldsToUpdate = actionData.fields || [primaryField];
      const displayText = actionData.displayText || `Update ${primaryField.label}`;
      
      // Add user message with displayText
      const userMsg = {
        id: `m${Date.now()}`,
        role: "user",
        text: displayText,
        name: "You",
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, userMsg]);
      
      // Store the selected fields for later validation
      setUpdateFieldsFormSelectedFields(allFieldsToUpdate);

      // Convert all fields to ui_fields format for UpdateSingleFieldAICard
      const uiFields = allFieldsToUpdate.map((field) => {
        // Determine if this is an activedirectory field
        const isActiveDirectory =
          field.dataType?.toLowerCase() === "activedirectory" ||
          field.dataType?.toLowerCase() === "active_directory" ||
          field.type?.toLowerCase() === "active_directory";

        return {
          field_name: field.fieldKey,
          updatedValue: "",
          type: isActiveDirectory ? "active_directory" : undefined,
          meta: {
            display_label: field.display_label || field.label,
            backend_field: field.fieldKey,
            type: isActiveDirectory ? "active_directory" : undefined,
            value_format:
              field.dataType === "Date"
                ? "date"
                : field.dataType === "dropdown"
                  ? "dropdown"
                  : field.dataType === "numerical"
                    ? "number"
                    : isActiveDirectory
                      ? "email"
                      : "text",
            dropdown: field.dataType === "dropdown",
            dropdown_options: field.fieldValueList || [],
          },
        };
      });

      setTimeout(() => {
        const aiResponse = {
          id: `m${Date.now()}`,
          role: "ai",
          type: "aiCard",
          ui_fields_flag: true,
          ui_fields: uiFields,
          header: { title: "GDS.ai Axon" },
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, aiResponse]);
      }, 500);
      return;
    }

    const newMsg = {
      id: `m${Date.now()}`,
      role: "user",
      text: actionText,
      name: "You",
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, newMsg]);

    // If action is to update multiple fields, show UpdateMultipleFieldsAICard
    if (actionText === "I want to update multiple fields at once") {
      setTimeout(() => {
        const aiResponse = {
          id: `m${Date.now()}`,
          role: "ai",
          type: "aiCard",
          updateMultipleFields: true,
          header: { title: "GDS.ai Axon" },
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, aiResponse]);
      }, 500);
      return;
    }

    // If action is "Action Taken", show UpdateSingleFieldAICard with selected fields
    if (actionText === "Action Taken" && actionData?.selectedFields) {
      // Store the selected fields for later validation
      setUpdateFieldsFormSelectedFields(actionData.selectedFields);

      // Convert selected fields to ui_fields format for UpdateSingleFieldAICard
      const uiFields = actionData.selectedFields.map((field) => {
        // Determine if this is an activedirectory field
        const isActiveDirectory =
          field.dataType?.toLowerCase() === "activedirectory" ||
          field.dataType?.toLowerCase() === "active_directory" ||
          field.type?.toLowerCase() === "active_directory";

        return {
          field_name: field.fieldKey,
          updatedValue: "",
          type: isActiveDirectory ? "active_directory" : undefined,
          meta: {
            display_label: field.display_label || field.label,
            backend_field: field.fieldKey,
            type: isActiveDirectory ? "active_directory" : undefined,
            value_format:
              field.dataType === "Date"
                ? "date"
                : field.dataType === "dropdown"
                  ? "dropdown"
                  : field.dataType === "numerical"
                    ? "number"
                    : isActiveDirectory
                      ? "email"
                      : "text",
            dropdown: field.dataType === "dropdown",
            dropdown_options: field.fieldValueList || [],
          },
        };
      });

      setTimeout(() => {
        const aiResponse = {
          id: `m${Date.now()}`,
          role: "ai",
          type: "aiCard",
          ui_fields_flag: true,
          ui_fields: uiFields,
          header: { title: "GDS.ai Axon" },
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, aiResponse]);
      }, 500);
      return;
    }

    // Send selected action as a query to the AI service.
    /* OLD VERSION - kept for reference, now extracted to sendMessageToAI and handling different action types
    const fetchAIResponseForAction = async () => {
      setIsAILoading(true);
      try {
        const response = await AIService.sendMessage({
          message: actionText,
          threadId: threadIdRef.current,
          userId: userEmail,
          orderId: orderId,
        });
        ...
      } catch (error) {
        ...
      } finally {
        setIsAILoading(false);
      }
    };
    fetchAIResponseForAction();
    */
    sendMessageToAI(actionText);

    // Notify parent component about the action
    if (onActionClick) {
      onActionClick(actionText);
    }
  };

  // Determine if chat should expand (simulating long chat taking header space)
  const isExpanded = useMemo(() => {
    return variant === "order-details" && messages.length > 3;
  }, [variant, messages.length]);

  // Only overlay/expand behind the header after the 1st user message
  const userMessageCount = useMemo(() => {
    return messages.filter((m) => m.role === "user").length;
  }, [messages]);

  // Ensure at least one AI message/card has appeared before overlaying
  const aiMessageCount = useMemo(() => {
    return messages.filter((m) => m.role === "ai" || m.type === "aiCard")
      .length;
  }, [messages]);

  const isTargetStatus = useMemo(() => {
    if (variant !== "order-details") return false;
    const status = orderDetails?.status;
    return status === "Data Triage" || status === "Assigned Request";
  }, [variant, orderDetails?.status]);

  // If no status header (CollapsibleStatus) is present, always expand to 619px
  const shouldExpand = (() => {
    // No header: always use tall chat
    if (variant === "order-details" && !hasStatusHeader) return true;

    // With header and target statuses: expand only after first AI + first user message
    if (variant === "order-details" && hasStatusHeader && isTargetStatus) {
      return aiMessageCount >= 1 && userMessageCount >= 1;
    }

    // Other statuses: fallback to long conversation expansion
    return isExpanded;
  })();

  // Auto-scroll to bottom on new messages
  const listRef = useRef(null);
  useEffect(() => {
    if (listRef.current) {
      // Smooth scroll to bottom with delay to handle AI card rendering
      const scrollToBottom = () => {
        if (listRef.current) {
          // Scroll to the actual content height
          listRef.current.scrollTo({
            top: listRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      };

      // Longer delay to account for scaled content rendering at 150% resolution
      setTimeout(scrollToBottom, 300);
    }
  }, [messages]);

  const handleFormValuesChange = (values) => {
    setUpdateFieldsFormValues(values);
  };

  if (variant === "order-details") {
    return (
      <div className={`order-chat-container ${shouldExpand ? "expanded" : ""}`}>
        <div
          ref={listRef}
          className={`order-chat-display ${
            hasStatusHeader ? "with-collapsible" : ""
          }  ${
            hasStatusHeader && orderDetails?.status === "Data Triage"
              ? "with-collapsible--tall"
              : ""
          } ${shouldExpand ? `expanded ${!isOpen ? "not_is_open" : ""}` : ""}`}
        >
          <div className="order-chat-messages">
            {/* Trail Logs Section - displayed at top of chat */}
            <TrailLogsSection trailLogs={trailLogs} />
            {messages.map((m) => {
              if (m.type === "genericSuper") {
                return (
                  <GenericSuperAICard
                    key={m.id}
                    message={m}
                    title={CHAT.AXON_AI}
                  />
                );
              }
              if (m.type === "fieldsUpdatePrompt") {
                return (
                  <RecommendedButtonFieldUpdateCard
                    key={m.id}
                    message={m}
                    onInputChange={handleFieldsUpdatePromptInputChange}
                    onSend={handleFieldsUpdatePromptSend}
                    disabled={
                      actionTakenTimestamp && m.timestamp < actionTakenTimestamp
                    }
                  />
                );
              }
              if (m.type === "aiCard") {
                // Disable buttons on cards that were created before "Action Taken" was clicked
                // OR when chat is disabled due to non-latest notification navigation
                const shouldDisableButtons =
                  (actionTakenTimestamp &&
                    m.timestamp < actionTakenTimestamp) ||
                  isChatDisabled;
                // Start disable recommended action changes 1st
                const shouldDisableRecommendedActionButtons =
                  isChatDisabled ||
                  // Start disable recommended action changes 2nd
                  //(currentOrderDetailsAiCardId !== null &&
                  //  m.id !== currentOrderDetailsAiCardId);
                  // end disable recommended action changes 2nd

                  // Start disable recommended action changes 2nd
                  (latestRecommendedActionCardId !== null &&
                    m.id !== latestRecommendedActionCardId); // new
                // end disable recommended action changes 2nd
                return (
                  <AIChat
                    key={m.id}
                    message={m}
                    orderDetails={orderDetails}
                    chatSource={source}
                    onActionClick={handleActionClick}
                    onFormValuesChange={handleFormValuesChange}
                    onRefreshOrderDetails={onRefreshOrderDetails}
                    disableButtons={shouldDisableButtons}
                    // start disable recommended action changes 1st
                    disableRecommendedActionButtons={
                      shouldDisableRecommendedActionButtons
                    }
                    // end disable recommended action changes 1st
                    hideRecommendedActions={isChatDisabled}
                  />
                );
              }
              return <UserChat key={m.id} message={m} />;
            })}
            {isAILoading && (
              <AILoadingCard
                message={{ role: "ai", header: { title: CHAT.AXON_AI } }}
              />
            )}
          </div>
        </div>

        <div className="order-chat-input">
          <div className="order-chat-input-row">
            <TextArea
              className="order-chat-textarea"
              placeholder={
                isChatDisabled
                  ? "Chat is disabled for this notification"
                  : isAILoading
                    ? PLACEHOLDERS.PLEASE_WAIT
                    : `Ask me anything about this order...`
              }
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              disabled={isAILoading || isChatDisabled}
            />
            {/* Region selector - HIDDEN for now but keeping code for future use.
               Default region "general" is still sent in API payload via selectedRegion state.
            {variant === "home" && (
              <div
                className="order-chat-region-selector"
                ref={regionDropdownRef}
              >
                <button
                  className="order-chat-region-trigger"
                  onClick={() => setIsRegionDropdownOpen((prev) => !prev)}
                  aria-label="Select super region"
                  type="button"
                  disabled={isChatDisabled}
                >
                  <GlobeIcon width={18} height={18} color="#737387" />
                  <span className="order-chat-region-text">
                    {selectedRegion?.name || "Knowledge Regions"}
                  </span>
                  <NavDownArrow width={12} height={12} color="#737387" />
                </button>
                {isRegionDropdownOpen && !isChatDisabled && (
                  <div className="order-chat-region-dropdown">
                    <div className="order-chat-region-dropdown-header">
                      <GlobeIcon width={18} height={18} color="#737387" />
                      <span>Knowledge Regions</span>
                    </div>
                    <div className="order-chat-region-dropdown-list">
                      {SUPER_REGIONS.map((region) => (
                        <button
                          key={region.id}
                          type="button"
                          className={`order-chat-region-item ${
                            selectedRegion?.id === region.id ? "active" : ""
                          }`}
                          onClick={() => {
                            dispatch(setSelectedRegion(region));
                            setIsRegionDropdownOpen(false);
                          }}
                        >
                          <span>{region.name}</span>
                          {selectedRegion?.id === region.id && (
                            <span className="order-chat-region-check">✓</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            */}
            <div className="order-chat-input-icons">
              {isAILoading ? (
                <button
                  className="chatbot-icon-button2"
                  aria-label="Stop response"
                  onClick={handleStopResponse}
                >
                  <StopIcon />
                </button>
              ) : (
                <button
                  className="chatbot-icon-button1"
                  aria-label="Send message"
                  onClick={onSend}
                  disabled={isChatDisabled}
                >
                  <SendIcon />
                </button>
              )}
            </div>
          </div>
          <div className="order-chat-suggestions">
            <button
              className="order-chat-chip"
              disabled={isChatDisabled}
              onClick={() => {
                if (isChatDisabled) return;
                const userMsg = {
                  id: `m${Date.now()}`,
                  role: "user",
                  text: CHAT_ACTIONS.ACTIVITY_LOG,
                  name: "You",
                  timestamp: Date.now(),
                };
                setMessages((prev) => [...prev, userMsg]);
                fetchActivityLog();
              }}
            >
              {CHAT_ACTIONS.ACTIVITY_LOG}
            </button>
            <button
              className="order-chat-chip"
              disabled={isChatDisabled}
              onClick={() => {
                if (isChatDisabled) return;
                const userMsg = {
                  id: `m${Date.now()}`,
                  role: "user",
                  text: CHAT_ACTIONS.UPDATE_ORDER_INFO,
                  name: "You",
                  timestamp: Date.now(),
                };
                setMessages((prev) => [...prev, userMsg]);

                setTimeout(() => {
                  const loadingMessage = {
                    id: `m${Date.now()}`,
                    role: "ai",
                    type: "aiCard",
                    loading: true,
                    loadingText: "Fetching Fields..",
                    header: { title: CHAT.AXON_AI },
                    timestamp: Date.now(),
                  };
                  setMessages((prev) => [...prev, loadingMessage]);

                  setTimeout(() => {
                    setMessages((prev) => {
                      const filtered = prev.filter(
                        (m) => m.id !== loadingMessage.id,
                      );
                      const aiResponse = {
                        id: `m${Date.now()}`,
                        role: "ai",
                        type: "aiCard",
                        updateOrderInfo: true,
                        header: { title: CHAT.AXON_AI },
                        timestamp: Date.now(),
                      };
                      return [...filtered, aiResponse];
                    });
                  }, CONTENT_DELAY);
                }, RESPONSE_DELAY);
              }}
            >
              Update Order Info
            </button>
            <button
              className="order-chat-chip-see-all"
              disabled={isChatDisabled}
              onClick={() => {
                if (isChatDisabled) return;
                handleSeeAllClick();
              }}
            >
              See All
            </button>
          </div>
          <div className="order-chats-disclaimer">
            <span>
              {CHAT_ACTIONS.DISCLAIMER}
              <CustomTooltipTop
                trigger={
                  <InfoCircleIcon
                    width="14.33"
                    height="14.33"
                    color="#85B9FD"
                  /> //color="#dedee2" />
                }
                normalText={CHAT_ACTIONS.DISCLAIMER_TOOLTIP}
              />
            </span>
          </div>
        </div>

        <MotifModal
          show={showDialog}
          onClose={handleCloseDialog}
          size="xl"
          className="chatbot-see-all-dialog"
        >
          <MotifModalHeader>
            <div className="sparks-icon-wrapper">
              <Sparks width={40} height={40} color="#FFE600" />
            </div>
          </MotifModalHeader>
          <MotifModalBody>
            <div className="smart-suggestions-section">
              <h2 className="smart-suggestions-title">
                {CHAT.SMART_SUGGESTIONS}
              </h2>
              <div className="smart-suggestions-buttons">
                {availableTabs.map((tab) => (
                  <button
                    key={tab}
                    className={`chatbot-suggestion-button ${activeTab === tab ? "active" : ""}`}
                    onClick={() => handleTabClick(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            <div className="all-actions-content">
              {currentSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  className="chatbot-dialog-action-button"
                  onClick={() => suggestion.action?.()}
                >
                  {suggestion.icon} {suggestion.text}
                </button>
              ))}
            </div>
          </MotifModalBody>
        </MotifModal>
      </div>
    );
  }

  // Handle region selection
  const handleRegionSelect = (region) => {
    dispatch(setSelectedRegion(region));
    setIsRegionDropdownOpen(false);
  };

  // Home variant
  return (
    <div
      className={`chatbot-container ${isDisabled ? "chatbot-disabled" : ""}`}
    >
      <div className="chatbot-textarea-wrapper">
        {/* Knowledge Regions dropdown - HIDDEN for now but keeping code for future use.
           Default region "general" is still sent in API payload via selectedRegion state.
        <div className="chatbot-region-selector" ref={regionDropdownRef}>
          <button
            className="chatbot-region-trigger"
            onClick={() =>
              !isDisabled && setIsRegionDropdownOpen((prev) => !prev)
            }
            aria-label="Select super region"
            type="button"
            disabled={isDisabled}
          >
            <GlobeIcon width={18} height={18} color="#737387" />
            <span className="chatbot-region-text">
              {selectedRegion?.name || "Knowledge Regions"}
            </span>
            <NavDownArrow width={12} height={12} color="#737387" />
          </button>
          {isRegionDropdownOpen && (
            <div className="chatbot-region-dropdown">
              <div className="chatbot-region-dropdown-header">
                <GlobeIcon width={18} height={18} color="#737387" />
                <span>Knowledge Regions</span>
              </div>
              <div className="chatbot-region-dropdown-list">
                {SUPER_REGIONS.map((region) => (
                  <button
                    key={region.id}
                    type="button"
                    className={`chatbot-region-item ${
                      selectedRegion?.id === region.id ? "active" : ""
                    }`}
                    onClick={() => handleRegionSelect(region)}
                  >
                    <span>{region.name}</span>
                    {selectedRegion?.id === region.id && (
                      <span className="chatbot-region-check">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        */}
        <TextArea
          placeholder={
            isDisabled ? PLACEHOLDERS.PLEASE_WAIT : PLACEHOLDERS.ASK_ME_ANYTHING
          }
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          disabled={isDisabled}
        />
        <div className="chatbot-icons">
          {isDisabled ? (
            <button
              className="chatbot-icon-button2"
              aria-label="Stop response"
              onClick={handleStopResponse}
            >
              <StopIcon />
            </button>
          ) : (
            <button
              className="chatbot-icon-button1"
              aria-label={ARIA_LABELS.SEND_MESSAGE}
              onClick={onSend}
              disabled={isDisabled}
            >
              <SendIcon />
            </button>
          )}
        </div>
      </div>

      {!isPlanMyDayMode && (
        <>
          <div className="chatbot-buttons-box">
            {/* Role-based inline suggestions (3 buttons) */}
            {roleBasedInlineSuggestions.map((suggestion, index) => (
              <button
                key={index}
                className={`chatbot-action-button ${index === 0 ? "yellow-button" : ""}`}
                onClick={() => !isDisabled && suggestion.action?.()}
                disabled={isDisabled}
              >
                {suggestion.icon} {suggestion.text}
              </button>
            ))}
            <button
              className="chatbot-action-button text-button"
              onClick={handleSeeAllClick}
              disabled={isDisabled}
            >
              {CHAT.SEE_ALL}
            </button>
          </div>

          {/* Disclaimer message */}
          <div className="chatbot-disclaimer">
            {/* <InfoCircleIcon width="16" height="16" color="#DEDEE2" /> */}
            {/*color="#85B9FD" />*/}
            {variant === "home" && !orderHome && (
              <span>
                {CHAT.DISCLAIMER}{" "}
                <a
                  href={CHAT.DISCLAIMER_LINK_URL1}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="chatbot-disclaimer-link"
                >
                  {CHAT.DISCLAIMER_LINK_TEXT1}
                </a>
                {CHAT.DISCLAIMER_SUFFIX}
                <a
                  href={CHAT.DISCLAIMER_LINK_URL2}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="chatbot-disclaimer-link"
                >
                  {CHAT.DISCLAIMER_LINK_TEXT2}
                </a>
              </span>
            )}
            <span>
              {CHAT_ACTIONS.DISCLAIMER}
              <CustomTooltipTop
                trigger={
                  <InfoCircleIcon
                    width="14.33"
                    height="14.33"
                    color="#85B9FD"
                  /> //color="#dedee2" />
                }
                normalText={CHAT_ACTIONS.DISCLAIMER_TOOLTIP}
              />
            </span>
          </div>

          <MotifModal
            show={showDialog}
            onClose={handleCloseDialog}
            size="auto"
            className="chatbot-see-all-dialog"
          >
            <MotifModalHeader>
              <div className="sparks-icon-wrapper">
                <Sparks width={40} height={40} color="#FFE600" />
              </div>
            </MotifModalHeader>
            <MotifModalBody>
              <div className="smart-suggestions-section">
                <h2 className="smart-suggestions-title">
                  {CHAT.SMART_SUGGESTIONS}
                </h2>
                <div className="smart-suggestions-buttons">
                  {availableTabs.map((tab) => (
                    <button
                      key={tab}
                      className={`chatbot-suggestion-button ${activeTab === tab ? "active" : ""}`}
                      onClick={() => handleTabClick(tab)}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>

              <div className="all-actions-content">
                {currentSuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    className="chatbot-dialog-action-button"
                    onClick={() => {
                      handleCloseDialog();
                      suggestion.action?.();
                    }}
                  >
                    {suggestion.icon} {suggestion.text}
                  </button>
                ))}
              </div>
            </MotifModalBody>
          </MotifModal>
        </>
      )}
    </div>
  );
};
